class FeedingAdvice {
  constructor(hostElement = document) {
    // Initialize variables
    this.feedingCalculationsData = window.QF.feedingCalculations;
    this.feedingJSON = this.feedingCalculationsData.weight_grouping;
    this.root = hostElement instanceof HTMLElement ? hostElement : document;

    // Object to hold dog-related values
    this.dogValues = {
      dogId: "", // ID of the dog
      dogImage: "", // Image of the dog
      dogName: "", // Name of the dog
      dogBreed: "", // Breed of the dog
      dogAge: "", // Age of the dog
      dogFoodType: "", // Type of food preferred by the dog
      dogDryFoodPreference: "", // Preferred dry food type
      dogFavourites: "", // Flavours preferred by the dog
      dogWeight: "", // Weight of the dog
      dogDob: "", // Date of birth of the dog
      dogActive: "", // Activity level of the dog
      dogSiblings: "", // Whether the dog has siblings
      dogRescue: "", // Whether the dog is a rescue
      dogBio: "", // Biography of the dog
      dogInstagram: "", // Instagram handle of the dog
      dogTiktok: "", // TikTok handle of the dog
      dogGender: "", // Gender of the dog
      dogNeutered: "", // Neutered status of the dog
      tagArray: [], // Array to hold tags
      tagString: "", // String representation of tags
      wetGrams: 0, // Grams of wet food
      wetGramsMin: 0, // Minimum grams of wet food
      wetGramsMax: 0, // Maximum grams of wet food
      dryGrams: 0, // Grams of dry food
      dryGramsMin: 0, // Minimum grams of dry food
      dryGramsMax: 0, // Maximum grams of dry food
      wetTrays: "", // Number of wet food trays
      wetTraysMin: "", // Minimum number of wet food trays
      wetTraysMax: "", // Maximum number of wet food trays
      dailyKcal: 0, // Daily calorie requirement
    };

    // Initialize current step and base URL for collections
    this.currentStep = 0;
    this.baseUrlCollections = "/collections/your-recommendations/?sort_by=manual";
    this.weightFoodData = "";
    this.activityAgeData = "";
    this.activityLevel = "";
    this.today = new Date();
    this.aWeek = 7 * 24 * 60 * 60 * 1000;
    this.QFConnectorURL = "/apps/qfconnector/";
    this.skipFormSubmissionOnLoad = false;

    // Initialize customer token
    this.customerToken = window.QF?.customer?.token || "";

    if (this.customerToken === null || this.customerToken === undefined) {
      this.fetchCustomerToken();
    }

    // Initialize UI elements
    this.initUI();
    this.stepValidationHandlers = this.buildStepValidationHandlers();
    this.setupEventListeners();

    // Initialize drag scrolling for tabs
    if (window.QF && window.QF.initDragScroll) {
      window.QF.initDragScroll(".fmf-modal .tabs ul");
    }
  }

  initUI() {
    const root = this.root;

    // Dog feeding preference radios
    this.dogFeedingPrefsRadios = root.querySelectorAll('input[name="dogs[0][dog_preferences]"]');
    this.dogDryPreferenceRadios = root.querySelectorAll(
      'input[name="dogs[0][dog_dry_preference]"]',
    );
    this.dogActiveElements = root.querySelectorAll("[fmf-form-dog-active]");

    // Dog DOB
    this.dogBirthdayInput = root.querySelector("#dog-dob");
    const maxDate = new Date().toISOString().split("T")[0];
    this.dogBirthdayInput.setAttribute("max", maxDate);
    this.dogBirthdayInput.setAttribute("required", "required");

    // Step nav
    this.prevButton = root.querySelector("[fmf-prev-step]");
    this.nextButton = root.querySelector("[fmf-next-step]");
    this.startOver = root.querySelector("[fmf-start-over]");
    this.viewResults = root.querySelector("[fmf-view-results]");
    this.addNew = root.querySelector("[fmf-add-new]");
    // Step navigation buttons
    this.startOver.style.display = "none";

    // Modal elements
    this.fmfModal = root.querySelector(".fmf-modal");
    this.fmfModalTab = root.querySelector(".fmf-modal .open-fmf");

    // Required inputs
    this.dogIdInput = root.querySelector('.fmf-modal input[name="dogs[0][id]"]');
    this.dogImage = root.querySelector(".fmf-modal [data-dog-image]");
    this.dogImageInput = root.querySelector('.fmf-modal input[name="dogs[0][dog_image]"]');
    this.dogNameInput = root.querySelector(".fmf-modal #dog-name");
    this.defaultDogName = this.dogNameInput?.getAttribute("placeholder")?.trim() || "Rufus";
    this.dogDobInput = root.querySelector(".fmf-modal #dog-dob");
    this.dogDobIsoInput = root.querySelector(".fmf-modal #dog-dob-iso");
    this.dogAgeInput = root.querySelector(".fmf-modal #dog-age");
    this.setCanonicalDogDob(this.dogDobIsoInput?.value || this.dogDobInput?.value || "");

    // Initialize breed inputs (will be updated after DOM is ready)
    this.refreshBreedInputs();
    this.availableBreeds = this.getAvailableBreeds();
    this.breedInputCounter = this.getMaxBreedInputCounter();
    this.expandBreedsButton = root.querySelector(".expand-breeds");
    this.dogWeightInput = root.querySelector(".fmf-modal #dog-weight");
    this.dogWeightLabel = root.querySelector('label[for="dog-weight"]');
    this.dogWeightHeading = root.querySelector(
      ".field-wrapper.field-sub.dog-weight-standard .heading-h3, .field-wrapper.field-sub.dog-weight-standard .h3",
    );
    this.dogActiveInput = root.querySelectorAll('.fmf-modal input[name="dogs[0][dog_activity]"]');
    this.dogGenderInput = root.querySelectorAll('.fmf-modal input[name="dogs[0][dog_gender]"]');
    this.dogNeuteredInput = root.querySelectorAll('.fmf-modal input[name="dogs[0][dog_neutered]"]');
    this.dogRescueInput = root.querySelectorAll('.fmf-modal input[name="dogs[0][dog_rescue]"]');
    this.dogSiblingsInput = root.querySelector('.fmf-modal input[name="dogs[0][dog_siblings]"]');
    this.dogBioInput = root.querySelector('.fmf-modal textarea[name="dogs[0][dog_bio]"]');
    this.dogInstagramInput = root.querySelector('.fmf-modal input[name="dogs[0][dog_instagram]"]');
    this.dogTiktokInput = root.querySelector('.fmf-modal input[name="dogs[0][dog_tiktok]"]');
    this.formErrorsElement = root.querySelector(".fmf-form-errors");
    this.dogFavouritesCheckboxes = root.querySelectorAll(
      '.fmf-modal input[name="dogs[0][dog_favourites][]"]',
    );
    this.dogFavouritesElements = root.querySelectorAll("[fmf-form-dog-flavours]");

    // Steps
    this.fmfForm = root.querySelector(".fmf-form form");
    this.fieldsets = root.querySelectorAll(".fmf-form fieldset");
    this.dogNamesSteps = root.querySelectorAll("[fmf-form-dog-name]");
    this.stepsCounter = root.querySelector(".steps-counter span");
    this.progressBar = root.querySelector(".steps-progress-bar-progress");
    this.stepsTotal = root.querySelector(".steps-total");
    this.currentStepElement = root.querySelector(".current-step");

    this.passportImgSrc = this.fmfForm.dataset.passportImgSrc;

    // Other
    ((this.warning = root.querySelector(".topper-warning")),
      (this.complementaryLink = root.querySelector(".complementary-link")),
      (this.puppyWarnings = root.querySelectorAll(".puppy-warning")),
      (this.puppywarning = root.querySelector(".puppy-warning")),
      (this.ageMismatchWarnings = root.querySelectorAll(".age-mismatch-warning")),
      (this.ageMismatchDetails = root.querySelectorAll("[data-age-mismatch-detail]")),
      (this.birthdayinfo = root.querySelector(".birthdayinfo")),
      (this.contactlink = root.querySelector(".contact-link")),
      (this.fieldsetWeight = root.querySelector('.fieldset[data-step="4"]')));
    this.topperRadio = root.querySelector("#dog-feeding-topper");
    this.emailMessage = root.querySelector("#email-message");
    this.emailInput = root.querySelector('.fmf-modal .account-tabs input[type="email"]');
    this.createAccountBtn = root.querySelector(
      ".fmf-modal .account-tabs .register-tab-content .submitForm",
    );

    // Set the ID of the dog
    this.dogValues.dogId = parseInt(this.dogIdInput.value);

    // Update progress bar
    this.updateProgressBar();

    const userLoggedIn = this.getStoredValue("userLoggedIn", "false");
    const passportSynced = this.getStoredValue("passportSynced", "false");

    if (userLoggedIn === "true" && passportSynced !== "true") {
      // Push PassportLocal to the server
      const storedForms = this.getStoredJSON("passportLocal", []);
      if (storedForms.length > 0) {
        const formData = new FormData();
        storedForms.map((form, index) => {
          Object.keys(form).forEach((key) => {
            if (form[key] !== null) {
              if (key === "dog_favourites") {
                // Split dog_favourites into individual values
                const values = form[key].split("|");
                values.forEach((value) => {
                  formData.append(`dogs[${index}][${key}][]`, value);
                });
              } else if (
                key !== "dog_image" &&
                key !== "customer_id" &&
                key !== "dog_passport_status"
              ) {
                formData.append(`dogs[${index}][${key}]`, form[key]);
              }
            }
          });

          formData.append(`dogs[${index}][dog_passport_status]`, "active");
          formData.append(`dogs[${index}][customer_id]`, window.QF.customer.id);
        });

        // Send the form data to the endpoint
        this.postFormData(this.apiUrl("passport"), formData, {
          headers: {
            Accept: "application/json",
          },
        }).catch((error) => console.error("Error:", error));

        this.setStoredValue("passportSynced", "true");
      }
    } else {
      // Clear passport storage if user logged out
      if (window.QF.logged_in === false) {
        this.setStoredValue("passportSynced", "false");
      }
    }
  }

  getStoredValue(key, fallback = null) {
    const value = localStorage.getItem(key);
    return value === null ? fallback : value;
  }

  getActiveTabContent() {
    return (
      this.root.querySelector(".tabs .tab-content.active") ||
      this.root.querySelector(".tabs .tab-content")
    );
  }

  getActiveTabInput(selector) {
    const activeTabContent = this.getActiveTabContent();
    return activeTabContent?.querySelector(selector) || this.root.querySelector(selector);
  }

  getStoredJSON(key, fallback = null) {
    const value = this.getStoredValue(key);
    if (!value) {
      return fallback;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      console.error(`Unable to parse localStorage value for ${key}`, error);
      return fallback;
    }
  }

  setStoredValue(key, value) {
    localStorage.setItem(key, value);
  }

  setStoredJSON(key, value) {
    this.setStoredValue(key, JSON.stringify(value));
  }

  parsePassportData(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    try {
      const parsedValue = typeof value === "string" ? JSON.parse(value) : value;

      if (parsedValue && typeof parsedValue === "object" && "value" in parsedValue) {
        return this.parsePassportData(parsedValue.value);
      }

      if (parsedValue && typeof parsedValue === "object") {
        const nestedPassportData =
          parsedValue.passports || parsedValue.dogs || parsedValue.data || parsedValue.items;

        if (nestedPassportData) {
          return this.parsePassportData(nestedPassportData);
        }

        if (parsedValue.id || parsedValue.dog_name) {
          return [parsedValue];
        }

        const objectValues = Object.entries(parsedValue)
          .filter(([, dogData]) => dogData && typeof dogData === "object")
          .map(([id, dogData]) => ({
            ...dogData,
            _passport_key: id,
          }));

        return objectValues.length ? objectValues : [];
      }

      return [];
    } catch (error) {
      return [];
    }
  }

  normalizePassportRecord(record, index = 0) {
    if (!record || typeof record !== "object") {
      return record;
    }

    return {
      ...record,
      id: record.id ?? record.dog_id ?? record.passport_id ?? record._passport_key ?? index + 1,
      dog_daily_kcal: record.dog_daily_kcal ?? record.dog_daily_kcals ?? 0,
      dog_dry_preference: record.dog_dry_preference ?? record.dog_dry_preferences ?? "no_answer",
    };
  }

  getStoredPassportDogs() {
    const passportData = this.parsePassportData(this.getStoredValue("passport"));
    if (passportData.length) {
      return passportData.map((dogData, index) => this.normalizePassportRecord(dogData, index));
    }

    const customerPassportData = this.parsePassportData(window.QF?.customer?.passports);
    if (customerPassportData.length) {
      const normalizedCustomerPassportData = customerPassportData.map((dogData, index) =>
        this.normalizePassportRecord(dogData, index),
      );
      this.setStoredJSON("passport", normalizedCustomerPassportData);
      return normalizedCustomerPassportData;
    }

    return [];
  }

  getDefaultPassportDog() {
    const passportDogs = this.getStoredPassportDogs();
    return passportDogs[0] || null;
  }

  isEmptyFMFSubmission(fmfData) {
    if (!fmfData || typeof fmfData !== "object") {
      return true;
    }

    const hasValue = (value) => {
      const normalizedValue = String(value ?? "").trim();
      return (
        normalizedValue !== "" &&
        normalizedValue !== "no_answer" &&
        normalizedValue !== "all recipes"
      );
    };

    return !(
      hasValue(fmfData.dog_name) ||
      hasValue(fmfData.dog_dob) ||
      hasValue(fmfData.dog_age) ||
      hasValue(fmfData.dog_weight_current) ||
      hasValue(fmfData.dog_preferences) ||
      hasValue(fmfData.dog_activity)
    );
  }

  updateFooterResultActions(forceResultsView = null) {
    const visibleFieldsetIndexes = this.getVisibleFieldsetIndexes();
    const firstVisibleIndex = visibleFieldsetIndexes[0] ?? 0;
    const lastVisibleIndex =
      visibleFieldsetIndexes[visibleFieldsetIndexes.length - 1] ?? this.fieldsets.length - 1;
    const isResultsView =
      typeof forceResultsView === "boolean"
        ? forceResultsView
        : this.currentStep === lastVisibleIndex;
    const isFirstStep = this.currentStep === firstVisibleIndex;
    const hasStoredPassports = this.getStoredPassportDogs().length > 0;
    const shouldShowViewResults = !isResultsView && isFirstStep && hasStoredPassports;

    if (this.startOver) {
      this.startOver.style.display = isResultsView ? "inline" : "none";
    }

    if (this.prevButton) {
      this.prevButton.style.display = shouldShowViewResults ? "none" : "inline";
    }

    if (!this.viewResults) {
      return;
    }

    this.viewResults.classList.toggle("hidden", !shouldShowViewResults);
    this.viewResults.style.display = shouldShowViewResults ? "inline" : "none";
  }

  updateExistingResultsLinkVisibility() {
    this.updateFooterResultActions();
  }

  idsMatch(idA, idB) {
    if (idA === null || idA === undefined || idB === null || idB === undefined) {
      return false;
    }
    return String(idA) === String(idB);
  }

  upsertPassportData(fmfData) {
    const passportData = this.getStoredJSON("passport", []);
    const safePassportData = Array.isArray(passportData) ? passportData : [];
    const normalizedFmfData = { ...(fmfData || {}) };

    if (
      normalizedFmfData.id === null ||
      normalizedFmfData.id === undefined ||
      normalizedFmfData.id === ""
    ) {
      normalizedFmfData.id = Date.now();
    }

    const existingObjectIndex = safePassportData.findIndex((obj) =>
      this.idsMatch(obj?.id, normalizedFmfData.id),
    );

    if (existingObjectIndex !== -1) {
      Object.assign(safePassportData[existingObjectIndex], normalizedFmfData);
    } else {
      safePassportData.push(normalizedFmfData);
    }

    return safePassportData;
  }

  persistPassportData(fmfData) {
    const passportData = this.upsertPassportData(fmfData);
    this.setStoredJSON("passport", passportData);

    if (window.QF.logged_in === false) {
      this.setStoredJSON("passportLocal", passportData);
    }

    return passportData;
  }

  persistFMFData(fmfData) {
    const normalizedFmfData = { ...(fmfData || {}) };
    if (!normalizedFmfData.dog_dry_preference && normalizedFmfData.dog_dry_preferences) {
      normalizedFmfData.dog_dry_preference = normalizedFmfData.dog_dry_preferences;
    }
    this.setStoredJSON("FMF", normalizedFmfData);
  }

  normalizeUnknownBreedValue(value) {
    const normalizedValue = String(value || "")
      .trim()
      .toLowerCase();
    if (
      normalizedValue === "unknown" ||
      normalizedValue === "i don't know" ||
      normalizedValue === "i_don't_know"
    ) {
      return "unknown";
    }
    return String(value || "").trim();
  }

  apiUrl(path) {
    const normalizedPath = String(path || "").replace(/^\/+/, "");
    return `${this.QFConnectorURL}${normalizedPath}`;
  }

  postJSON(
    url,
    payload,
    {
      headers = {},
      checkOk = false,
      mode,
      contentType = "application/json",
      parseJson = true,
    } = {},
  ) {
    const mergedHeaders = {
      Accept: "application/json",
      ...headers,
    };

    if (contentType) {
      mergedHeaders["Content-type"] = contentType;
    }

    const requestOptions = {
      method: "POST",
      headers: mergedHeaders,
      body: JSON.stringify(payload),
    };

    if (mode) {
      requestOptions.mode = mode;
    }

    return fetch(url, requestOptions).then((response) => {
      if (checkOk && !response.ok) {
        throw { status: response.status, message: response.statusText };
      }
      if (!parseJson) {
        return response;
      }
      return response.json();
    });
  }

  postFormData(url, formData, { headers = {}, checkOk = false } = {}) {
    return fetch(url, {
      method: "POST",
      headers,
      body: formData,
    }).then((response) => {
      if (checkOk && !response.ok) {
        throw { status: response.status, message: response.statusText };
      }
      return response.json();
    });
  }

  formatIsoDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  parseDogDob(value) {
    if (!value || typeof value !== "string") {
      return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    // ISO: YYYY-MM-DD
    let match = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      const parsedDate = new Date(year, month - 1, day);

      if (
        parsedDate.getFullYear() === year &&
        parsedDate.getMonth() === month - 1 &&
        parsedDate.getDate() === day
      ) {
        return parsedDate;
      }
      return null;
    }

    // Picker-style: DD/MM/YYYY / DD-MM-YYYY / DD MM YYYY
    match = trimmedValue.match(/^(\d{2})[\/\-\s](\d{2})[\/\-\s](\d{4})$/);
    if (match) {
      const day = Number(match[1]);
      const month = Number(match[2]);
      const year = Number(match[3]);
      const parsedDate = new Date(year, month - 1, day);

      if (
        parsedDate.getFullYear() === year &&
        parsedDate.getMonth() === month - 1 &&
        parsedDate.getDate() === day
      ) {
        return parsedDate;
      }
      return null;
    }

    return null;
  }

  normalizeDogDob(value) {
    const parsedDate = this.parseDogDob(value);
    return parsedDate ? this.formatIsoDate(parsedDate) : "";
  }

  formatDogDobForDisplay(value) {
    const parsedDate = this.parseDogDob(value);
    if (!parsedDate) {
      return "";
    }

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();
    return `${day}/${month}/${year}`;
  }

  setCanonicalDogDob(value = "") {
    const normalizedDogDob = this.normalizeDogDob(value);
    if (this.dogDobIsoInput) {
      this.dogDobIsoInput.value = normalizedDogDob;
    }
    return normalizedDogDob;
  }

  getCanonicalDogDob() {
    const normalizedDogDob = this.normalizeDogDob(
      this.dogDobIsoInput?.value || this.dogDobInput?.value || this.dogValues.dogDob || "",
    );

    if (this.dogDobIsoInput) {
      this.dogDobIsoInput.value = normalizedDogDob;
    }

    return normalizedDogDob;
  }

  normalizeDogAge(value) {
    const allowedDogAges = ["1-7 years adult", "7+ senior", "8 - 52 weeks puppy"];
    if (typeof value !== "string") {
      return "";
    }

    const trimmedValue = value.trim();
    return allowedDogAges.includes(trimmedValue) ? trimmedValue : "";
  }

  deriveDogAgeFromDob(dogDob) {
    const parsedDob = this.parseDogDob(this.normalizeDogDob(dogDob));
    if (!parsedDob) {
      return "";
    }

    const diffWeeks = Math.floor(Math.abs(this.today - parsedDob) / this.aWeek);

    if (diffWeeks >= 364) {
      return "7+ senior";
    } else if (diffWeeks >= 52) {
      return "1-7 years adult";
    }
    return "8 - 52 weeks puppy";
  }

  refreshBreedInputs() {
    // Re-query breed inputs in case they were added dynamically
    this.dogBreedInputs = this.root.querySelectorAll(".fmf-modal .breed-input");
    this.breedSelectionInputs = this.root.querySelectorAll(
      '.fmf-modal input[name="breedSelection"]',
    );
  }

  getAvailableBreeds() {
    const breedsScript = this.root.querySelector("script[fmf-breeds-data]");
    if (!breedsScript) {
      return [];
    }

    try {
      const parsedBreeds = JSON.parse(breedsScript.textContent || "[]");
      return Array.isArray(parsedBreeds) ? parsedBreeds : [];
    } catch (error) {
      console.error("Could not parse breed data", error);
      return [];
    }
  }

  getMaxBreedInputCounter() {
    const counters = Array.from(this.dogBreedInputs)
      .map((input) => Number(input.getAttribute("data-breed-number")))
      .filter((counter) => !Number.isNaN(counter));

    return counters.length ? Math.max(...counters) : 0;
  }

  setupBreedEventListeners() {
    // Set up breed selection event listeners
    this.breedSelectionInputs.forEach((radio) => {
      if (radio.dataset.breedSelectionListenerAttached === "true") return;
      radio.addEventListener("change", this.handleBreedSelectionChange.bind(this));
      radio.dataset.breedSelectionListenerAttached = "true";
    });

    const unknownBreedRadio = this.root.querySelector("#unknownBreed");
    if (unknownBreedRadio && unknownBreedRadio.dataset.breedUnknownToggleAttached !== "true") {
      unknownBreedRadio.dataset.breedUnknownToggleAttached = "true";
    }

    this.syncBreedSelectionState();

    // Set up breed input event listeners and autocomplete
    this.dogBreedInputs.forEach((input) => {
      if (input.dataset.breedInputListenerAttached !== "true") {
        input.addEventListener("input", this.handleBreedInputChange.bind(this));
        input.dataset.breedInputListenerAttached = "true";
      }

      this.attachAutocompleteToInput(input);
    });

    if (
      this.expandBreedsButton &&
      this.expandBreedsButton.dataset.breedExpandListenerAttached !== "true"
    ) {
      this.expandBreedsButton.addEventListener("click", this.handleAddBreedField.bind(this));
      this.expandBreedsButton.dataset.breedExpandListenerAttached = "true";
    }
  }

  clearBreedInputs() {
    this.dogBreedInputs.forEach((input) => {
      input.value = "";
      input.setCustomValidity("");
    });
  }

  setBreedChooserVisibility(isVisible) {
    const breedChoosers = this.root.querySelectorAll(".subOption.no-checkbox");
    if (!breedChoosers || breedChoosers.length === 0) {
      return;
    }

    breedChoosers.forEach((breedChooser) => {
      breedChooser.style.display = isVisible ? "block" : "none";
    });
  }

  syncBreedSelectionState() {
    this.breedSelectionInputs.forEach((input) => {
      input.dataset.isSelected = input.checked ? "true" : "false";
    });
  }

  getSelectedBreedType() {
    const checkedBreedTypeInput = this.root.querySelector('input[name="breedSelection"]:checked');
    if (checkedBreedTypeInput?.value) {
      return checkedBreedTypeInput.value;
    }

    // Some FMF variants only render an "unknown breed" checkbox and
    // no explicit single/multiple controls.
    const hasExplicitBreedTypeControls =
      this.root.querySelectorAll(
        'input[name="breedSelection"][value="single"], input[name="breedSelection"][value="multiple"]',
      ).length > 0;
    if (hasExplicitBreedTypeControls) {
      return "";
    }

    if (this.root.querySelector(".single-breed-container .breed-input")) {
      return "single";
    }

    if (this.root.querySelector(".multiple-breed-container .breed-input")) {
      return "multiple";
    }

    return "";
  }

  attachAutocompleteToInput(input) {
    if (!input || input.dataset.autocompleteAttached === "true") {
      return;
    }

    input.dataset.autocompleteAttached = "true";

    let activeIndex = -1;
    const suggestionsId = `${input.getAttribute("data-breed-number") || "breed"}-suggestions`;

    const clearSuggestions = () => {
      this.root.querySelectorAll(".breed-suggestions").forEach((list) => list.remove());
      activeIndex = -1;
    };

    const updateActiveItem = (items) => {
      items.forEach((item, index) => {
        item.style.backgroundColor = index === activeIndex ? "#f0f0f0" : "white";
      });
    };

    const showSuggestions = (matches) => {
      const suggestionsList = document.createElement("div");
      suggestionsList.id = suggestionsId;
      suggestionsList.className = "breed-suggestions";
      suggestionsList.style.cssText = `
                position: absolute;
                background: white;
                border: 1px solid #ccc;
                max-height: 200px;
                overflow-y: auto;
                font-family: var(--heading-font-family);
                font-weight: 400;
                z-index: 1000;
                width: 100%;
                top: 40px;
            `;

      matches.forEach((breed, index) => {
        const item = document.createElement("div");
        item.className = "suggestion-item";
        item.textContent = breed;
        item.style.cssText = `
                    padding: 8px 12px;
                    cursor: pointer;
                    border-bottom: 1px solid #eee;
                `;

        // Prevent input blur from removing suggestions before click selection completes.
        item.addEventListener("mousedown", (event) => {
          event.preventDefault();
        });

        item.addEventListener("click", () => {
          input.value = breed;
          this.validateBreedInputAgainstList(input);
          clearSuggestions();
          this.updateDogBreedValue();
        });

        item.addEventListener("mouseenter", () => {
          activeIndex = index;
          updateActiveItem(suggestionsList.querySelectorAll(".suggestion-item"));
        });

        suggestionsList.appendChild(item);
      });

      input.parentNode.style.position = "relative";
      input.parentNode.appendChild(suggestionsList);
    };

    input.addEventListener("input", (event) => {
      const searchTerm = event.target.value.trim();
      clearSuggestions();

      if (!searchTerm || this.availableBreeds.length === 0) return;

      const matches = this.availableBreeds
        .filter((breed) => breed.toLowerCase().includes(searchTerm.toLowerCase()))
        .slice(0, 10);

      if (matches.length > 0) {
        showSuggestions(matches);
      }
    });

    input.addEventListener("keydown", (event) => {
      const suggestionsList = this.root.querySelector(`[id="${suggestionsId}"]`);
      if (!suggestionsList) return;

      const items = suggestionsList.querySelectorAll(".suggestion-item");

      if (event.key === "ArrowDown") {
        event.preventDefault();
        activeIndex = Math.min(activeIndex + 1, items.length - 1);
        updateActiveItem(items);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        activeIndex = Math.max(activeIndex - 1, -1);
        updateActiveItem(items);
      } else if (event.key === "Enter" && activeIndex >= 0) {
        event.preventDefault();
        items[activeIndex].click();
      } else if (event.key === "Escape") {
        clearSuggestions();
      }
    });

    input.addEventListener("blur", () => {
      setTimeout(() => clearSuggestions(), 100);
    });
  }

  handleAddBreedField() {
    const container = this.root.querySelector(".multiple-breed-container");
    const button = container?.querySelector(".expand-breeds");

    if (!container || !button) return;

    this.breedInputCounter += 1;

    const wrapper = document.createElement("div");
    wrapper.className = "breed-field-wrapper";
    wrapper.style.display = "flex";
    wrapper.style.alignItems = "center";

    const newInput = document.createElement("input");
    newInput.type = "text";
    newInput.name = "dogs[0][dog_breed][]";
    newInput.className = "breed-input";
    newInput.placeholder = "Additional breed...";
    newInput.setAttribute("data-breed-number", String(this.breedInputCounter));

    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.textContent = "×";
    removeButton.className = "remove-breed-field";
    removeButton.style.marginLeft = "8px";
    removeButton.style.padding = "5px 8px";

    removeButton.addEventListener("click", () => {
      wrapper.remove();
      this.refreshBreedInputs();
      this.updateDogBreedValue();
    });

    wrapper.appendChild(newInput);
    wrapper.appendChild(removeButton);
    container.insertBefore(wrapper, button);

    this.refreshBreedInputs();
    newInput.addEventListener("input", this.handleBreedInputChange.bind(this));
    newInput.dataset.breedInputListenerAttached = "true";
    this.attachAutocompleteToInput(newInput);
  }

  bindRadioGroupListeners(radioInputs, handler) {
    if (
      !radioInputs ||
      typeof radioInputs.forEach !== "function" ||
      typeof handler !== "function"
    ) {
      return;
    }

    radioInputs.forEach((radio) => {
      radio.addEventListener("change", handler);
    });
  }

  bindDogValueRadioGroup(radioInputs, dogValueKey, fallbackValue = "no_answer") {
    this.bindRadioGroupListeners(radioInputs, (event) => {
      this.handleDogValueRadioChange(event, dogValueKey, fallbackValue);
    });
  }

  handleDogValueRadioChange(event, dogValueKey, fallbackValue = "no_answer") {
    const radioButton = event?.target;
    this.dogValues[dogValueKey] = radioButton?.checked ? radioButton.value : fallbackValue;
  }

  // Setup event listeners
  setupEventListeners() {
    // Event listener for dog feeding preference radios
    this.dogFeedingPrefsRadios.forEach((radio) => {
      radio.addEventListener("change", this.handleDogPreferencesChange.bind(this));
    });
    // Event listener for dog name change (delegated so dynamically-added tabs are supported)
    this.root.addEventListener("input", (event) => {
      if (event.target && event.target.matches('input[name="dogs[0][dog_name]"]')) {
        this.handleDogNameChange(event);
      }
    });
    // Event listener for dog birthday input
    const handleDogBirthdayChange = this.handleDogBirthdayChange.bind(this);
    this.dogBirthdayInput.addEventListener("input", handleDogBirthdayChange);
    this.dogBirthdayInput.addEventListener("change", handleDogBirthdayChange);
    // Event listener for dog weight input
    this.dogWeightInput.addEventListener("input", this.handleDogWeightChange.bind(this));
    // Event listener for dog siblings change
    this.dogSiblingsInput.addEventListener("change", this.handleDogSiblingChange.bind(this));
    // Event listener for previous button click
    this.prevButton.addEventListener("click", this.handlePrevButtonClick.bind(this));
    // Event listener for next button click
    this.nextButton.addEventListener("click", this.handleNextButtonClick.bind(this));

    this.startOver.addEventListener("click", this.handleResetClick.bind(this));
    if (this.viewResults) {
      this.viewResults.addEventListener("click", this.showExistingResults.bind(this));
    }
    this.addNew.addEventListener("click", this.handleResetClick.bind(this));

    this.bindRadioGroupListeners(this.dogActiveInput, this.handleDogActivityRadios.bind(this));
    this.bindDogValueRadioGroup(this.dogGenderInput, "dogGender");
    this.bindDogValueRadioGroup(this.dogNeuteredInput, "dogNeutered");
    this.bindDogValueRadioGroup(this.dogRescueInput, "dogRescue");

    document.addEventListener("keydown", this.handleKeyDown.bind(this));

    if (this.emailInput) {
      this.emailInput.addEventListener("input", this.checkExistingEmail.bind(this));
    }

    this.dogImageInput.addEventListener("change", (event) => {
      const dogImagePreview = this.root.querySelector("[data-dog-image]");
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          dogImagePreview.src = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    });

    this.dogValues.dogImage = this.root.querySelector("[data-dog-image]").src;

    // Breed listeners must be attached before loading saved state,
    // because initialStorageSetup dispatches breed selection changes.
    this.refreshBreedInputs();
    this.setupBreedEventListeners();

    this.initialStorageSetup();
    this.populateTabForms();
    this.updateExistingResultsLinkVisibility();
    this.bindTabs();
    this.bindAccountTabs();
    this.datePicker();
    this.generateCollectionUrl();
    this.initKlaviyoForm();
  }

  datePicker() {
    const today = new Date();
    const startYear = new Date(today.getFullYear() - 15);
    const dogDobInput = this.root.querySelector("#dog-dob");
    const dogAgeSelect = this.root.querySelector("#dog-age");
    const refreshDobState = () => {
      if (!dogDobInput) return;
      this.handleDogBirthdayChange({ target: dogDobInput });
    };

    const picker = this.createDogDobPicker(dogDobInput, startYear);
    if (picker && typeof picker.on === "function") {
      picker.on("selected", () => {
        refreshDobState();
      });
    }

    if (!dogAgeSelect || !dogDobInput) {
      return;
    }

    dogAgeSelect.addEventListener("change", (event) => {
      // Ignore synthetic changes dispatched by handleDogBirthdayChange to prevent loops.
      if (event && event.isTrusted === false) {
        return;
      }

      this.handleDogAgeSelectChange({
        selectedAge: dogAgeSelect.value,
        dogDobInput,
        picker,
        today,
        refreshDobState,
      });
    });
  }

  createDogDobPicker(dogDobInput, startYear) {
    if (!dogDobInput) {
      return null;
    }

    const isPageEmbed = this.root.querySelector(".fmf-modal")?.classList.contains("fmf-page-embed");

    return new Litepicker({
      firstDay: 1,
      format: "DD/MM/YYYY",
      autoApply: true,
      hideOnBodyClick: true,
      showTooltip: false,
      mobileFriendly: true,
      singleMode: true,
      separator: "/",
      zIndex: isPageEmbed ? 50 : 102,
      dropdowns: {
        years: {
          min: startYear,
          max: null,
        },
        months: true,
      },
      element: dogDobInput,
    });
  }

  setDogDobInputState(dogDobInput, { disabled, required }) {
    if (!dogDobInput) {
      return;
    }

    dogDobInput.disabled = disabled;
    if (required) {
      dogDobInput.setAttribute("required", "required");
      return;
    }

    dogDobInput.removeAttribute("required");
  }

  toggleNeuteredOptionsVisibility(dogAge = "") {
    const isPuppy = dogAge === "8 - 52 weeks puppy";
    const neuteredFields = Array.from(this.dogNeuteredInput || [])
      .map((input) => input.closest(".field.checkbox"))
      .filter(Boolean);

    neuteredFields.forEach((field) => {
      field.style.display = isPuppy ? "none" : "";
    });

    if (isPuppy) {
      this.dogNeuteredInput.forEach((input) => {
        input.checked = false;
      });
      this.dogValues.dogNeutered = "";
    }
  }

  toggleDryPreferenceOptionsVisibility(dogAge = "") {
    const isPuppy = dogAge === "8 - 52 weeks puppy";
    const isAdultOrSenior = dogAge === "1-7 years adult" || dogAge === "7+ senior";
    const coldPressedInput = this.root.querySelector("#dog-dry-pref-cold-pressed");
    const lightlyBakedInput = this.root.querySelector("#dog-dry-pref-lightly-baked");
    const lightlyBakedField =
      lightlyBakedInput?.closest(".field.checkbox") ||
      this.root.querySelector(".dry-pref-lightly-baked-option");
    const puppyMessage = this.root.querySelector(".dry-pref-puppy-message");
    const adultMessage = this.root.querySelector(".dry-pref-adult-message");

    this.dogDryPreferenceRadios.forEach((radio) => {
      const isLightlyBaked =
        radio.id === "dog-dry-pref-lightly-baked" || radio.value === "lightly_baked";
      radio.disabled = isPuppy && isLightlyBaked;
    });

    if (lightlyBakedField) {
      lightlyBakedField.style.opacity = isPuppy ? "0.5" : "";
      lightlyBakedField.style.pointerEvents = "";
      lightlyBakedField.style.cursor = isPuppy ? "not-allowed" : "";
    }

    if (puppyMessage) {
      puppyMessage.style.display = isPuppy ? "block" : "none";
    }
    if (adultMessage) {
      adultMessage.style.display = isAdultOrSenior ? "block" : "none";
    }

    if (isPuppy) {
      if (lightlyBakedInput?.checked) {
        lightlyBakedInput.checked = false;
      }

      if (coldPressedInput) {
        coldPressedInput.disabled = false;
        coldPressedInput.checked = true;
        this.dogValues.dogDryFoodPreference = coldPressedInput.id;
      }
    }
  }

  toggleDogWeightLabel(dogAge = "") {
    const isPuppy = dogAge === "8 - 52 weeks puppy";

    if (!this.dogWeightLabel) {
      return;
    }

    this.dogWeightLabel.textContent = isPuppy
      ? "What is dog's expected adult weight?"
      : "How much does your dog weigh?";

    if (this.dogWeightHeading) {
      const dogNameSpan = this.dogWeightHeading.querySelector("[fmf-form-dog-name]");
      if (dogNameSpan) {
        this.dogWeightHeading.innerHTML = isPuppy
          ? `What is ${dogNameSpan.outerHTML}'s expected adult weight?`
          : `How much does ${dogNameSpan.outerHTML} weigh?`;
      } else {
        this.dogWeightHeading.textContent = isPuppy
          ? "What is dog's expected adult weight?"
          : "How much does your dog weigh?";
      }
    }
  }

  deriveDobFromAgeSelection(selectedAge, today) {
    if (selectedAge === "1-7 years adult") {
      return new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
    }
    if (selectedAge === "7+ senior") {
      return new Date(today.getFullYear() - 7, today.getMonth(), today.getDate());
    }
    if (selectedAge === "8 - 52 weeks puppy") {
      return new Date(today.getTime() - 8 * this.aWeek);
    }

    return null;
  }

  updateLitepickerDate(picker, formattedDate) {
    if (!picker || typeof picker.setDate !== "function") {
      return;
    }

    picker.setDate(formattedDate);
  }

  handleDogAgeSelectChange({ selectedAge, dogDobInput, picker, today, refreshDobState }) {
    this.toggleNeuteredOptionsVisibility(selectedAge);
    this.toggleDryPreferenceOptionsVisibility(selectedAge);
    this.toggleDogWeightLabel(selectedAge);

    if (selectedAge === "unknown") {
      dogDobInput.value = "";
      this.setDogDobInputState(dogDobInput, { disabled: true, required: false });
      this.setCanonicalDogDob("");
      this.dogValues.dogDob = "";
      refreshDobState();
      return;
    }

    this.setDogDobInputState(dogDobInput, { disabled: false, required: true });

    const newDate = this.deriveDobFromAgeSelection(selectedAge, today);
    if (newDate) {
      const isoDogDob = this.formatIsoDate(newDate);
      dogDobInput.value = this.formatDogDobForDisplay(isoDogDob) || dogDobInput.value;
      this.setCanonicalDogDob(isoDogDob);
      this.updateLitepickerDate(picker, dogDobInput.value);
    }

    refreshDobState();
  }

  handleDogPreferencesChange(event) {
    const type = event.target.getAttribute("id");

    if (type === "dog-feeding-topper") {
      this.warning.classList.add("active");
      this.complementaryLink.classList.add("active");
      this.nextButton.classList.add("hide");
    } else {
      this.warning.classList.remove("active");
      this.complementaryLink.classList.remove("active");
      this.nextButton.classList.remove("hide");
    }
  }

  // Add new method to handle breed selection type changes
  handleBreedSelectionChange(event) {
    const selectedType = event.target.value;
    const singleContainers = this.root.querySelectorAll(".single-breed-container");
    const multipleContainers = this.root.querySelectorAll(".multiple-breed-container");

    // Clear all breed inputs when selection type changes
    this.clearBreedInputs();
    this.dogValues.dogBreed = "";

    // Show/hide appropriate containers
    if (selectedType === "single") {
      singleContainers.forEach((singleContainer) => {
        singleContainer.style.display = "block";
      });
      multipleContainers.forEach((multipleContainer) => {
        multipleContainer.style.display = "none";
      });
      this.setBreedChooserVisibility(true);
    } else if (selectedType === "multiple") {
      singleContainers.forEach((singleContainer) => {
        singleContainer.style.display = "none";
      });
      multipleContainers.forEach((multipleContainer) => {
        multipleContainer.style.display = "flex";
      });
      this.setBreedChooserVisibility(false);
    } else if (selectedType === "unknown") {
      const isChecked = !!event.target.checked;
      singleContainers.forEach((singleContainer) => {
        singleContainer.style.display = isChecked ? "none" : "block";
      });
      multipleContainers.forEach((multipleContainer) => {
        multipleContainer.style.display = "none";
      });
      this.setBreedChooserVisibility(!isChecked);
      this.dogValues.dogBreed = isChecked ? "unknown" : "";
      if (isChecked) {
        this.formErrorsElement.textContent = "";
        this.dogBreedInputs.forEach((input) => {
          input.removeAttribute("required");
          input.setCustomValidity("");
        });
        this.breedSelectionInputs.forEach((input) => {
          input.removeAttribute("required");
        });
      }
    }

    this.syncBreedSelectionState();
  }

  // Add new method to handle breed input changes
  handleBreedInputChange(event) {
    const input = event.target;
    this.validateBreedInputAgainstList(input);

    // Update the breed values when inputs change
    this.updateDogBreedValue();

    // If this is a new input that was added dynamically, make sure it has autocomplete
    if (!input.hasAttribute("data-autocomplete-attached")) {
      this.attachAutocompleteToInput(input);
      input.setAttribute("data-autocomplete-attached", "true");
    }
  }

  validateBreedInputAgainstList(input) {
    if (!input) {
      return true;
    }

    const value = input.value.trim();
    if (value === "") {
      input.setCustomValidity("");
      return true;
    }

    if (!Array.isArray(this.availableBreeds) || this.availableBreeds.length === 0) {
      input.setCustomValidity("");
      return true;
    }

    const matchedBreed = this.availableBreeds.find(
      (breed) => breed.toLowerCase() === value.toLowerCase(),
    );

    if (!matchedBreed) {
      input.setCustomValidity("Please select a breed from the list.");
      return false;
    }

    input.value = matchedBreed;
    input.setCustomValidity("");
    return true;
  }

  // Add new method to collect and format breed values
  updateDogBreedValue() {
    // Re-query each time so dynamically added breed fields are included.
    this.refreshBreedInputs();

    const selectedBreedType = this.getSelectedBreedType();

    if (!selectedBreedType) {
      this.dogValues.dogBreed = "";
      return;
    }

    if (selectedBreedType === "unknown") {
      this.dogValues.dogBreed = "unknown";
      return;
    }

    // Collect values from the selected breed mode (single/multiple),
    // not based on visibility, because previous steps are hidden later.
    const breedInputs = Array.from(this.dogBreedInputs).filter((input) => {
      if (selectedBreedType === "single") {
        return !!input.closest(".single-breed-container");
      }
      if (selectedBreedType === "multiple") {
        return !!input.closest(".multiple-breed-container");
      }
      return false;
    });

    const breedValues = breedInputs
      .map((input) => {
        const value = input.value.trim();
        if (value === "") return "";
        this.validateBreedInputAgainstList(input);
        return input.value.trim();
      })
      .filter((value) => value !== "");
    this.dogValues.dogBreed = breedValues.join(" / ");
  }

  isPuppyOrSeniorAge(dogAgeValue) {
    return dogAgeValue === "7+ senior" || dogAgeValue === "8 - 52 weeks puppy";
  }

  isSeniorAge(dogAgeValue) {
    return dogAgeValue === "7+ senior";
  }

  isPuppyAge(dogAgeValue) {
    return dogAgeValue === "8 - 52 weeks puppy";
  }

  getFirstAvailableDefaultData(defaultKey) {
    const activityAgeGroups = this.feedingJSON?.["activity-age"];
    if (!Array.isArray(activityAgeGroups)) {
      return null;
    }

    const matchingGroup = activityAgeGroups.find((group) => group?.[defaultKey]?.[0]);
    return matchingGroup?.[defaultKey]?.[0] || null;
  }

  getAdultSedentaryWetDefaultData() {
    const activityAgeGroups = this.feedingJSON?.["activity-age"];
    if (!Array.isArray(activityAgeGroups)) {
      return null;
    }

    const seniorGroup = activityAgeGroups.find((group) => {
      const description = String(group?.description || "").toLowerCase();
      return description.includes("senior") && group?.["wet-default"]?.[0];
    });

    return seniorGroup?.["wet-default"]?.[0] || this.getFirstAvailableDefaultData("wet-default");
  }

  getAdultWetDefaultData() {
    const activityAgeGroups = this.feedingJSON?.["activity-age"];
    if (!Array.isArray(activityAgeGroups)) {
      return null;
    }

    const adultGroup = activityAgeGroups.find((group) => {
      const ageValue = String(group?.age || "")
        .toLowerCase()
        .trim();
      return ageValue === "1-7 yrs" && group?.["wet-default"]?.[0];
    });

    return adultGroup?.["wet-default"]?.[0] || this.getFirstAvailableDefaultData("wet-default");
  }

  isPuppyActivityGroup(group) {
    const normalize = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    const ageValue = normalize(group?.age);
    const description = normalize(group?.description);
    const title = normalize(group?.title || group?.name || group?.label);

    return [ageValue, description, title].some(
      (value) =>
        value.includes("puppy") ||
        value.includes("852weeks") ||
        value.includes("8to52weeks") ||
        value.includes("8weeks52weeks"),
    );
  }

  isPuppyActivityAgeGroup(group) {
    const ageValue = String(group?.age || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
    return (
      ageValue.includes("852weeks") ||
      ageValue.includes("8to52weeks") ||
      ageValue.includes("8weeks52weeks")
    );
  }

  getPuppyActivityData() {
    const activityAgeGroups = this.feedingJSON?.["activity-age"];
    if (!Array.isArray(activityAgeGroups)) {
      return null;
    }

    return (
      activityAgeGroups.find((group) => this.isPuppyActivityAgeGroup(group)) ||
      activityAgeGroups.find((group) => this.isPuppyActivityGroup(group)) ||
      null
    );
  }

  resolveActivityAgeIndex({ dogAge, dogActive, activityLevelId } = {}) {
    const resolvedDogAge = dogAge ?? this.dogAgeInput.value;
    if (this.isPuppyOrSeniorAge(resolvedDogAge)) {
      return 0;
    }

    const activityByLevelId = {
      "dog-active-sedentary": 0,
      "dog-active-light": 1,
      "dog-active-active": 2,
      "dog-active-very": 2,
    };

    if (activityLevelId && activityByLevelId[activityLevelId] !== undefined) {
      return activityByLevelId[activityLevelId];
    }

    const activityByValue = {
      sedentary: 0,
      lightly_active: 1,
      active: 2,
    };

    return activityByValue[dogActive] ?? -1;
  }

  syncActivityAgeAndWeightFoodData(activityIndex) {
    const resolvedDogAge = this.dogAgeInput?.value || this.dogValues?.dogAge || "";
    if (this.isPuppyAge(resolvedDogAge)) {
      this.activityAgeData = this.getPuppyActivityData() || "";
      this.weightFoodData = this.getWeightFoodData(this.activityAgeData, this.dogValues.dogWeight);
      return !!this.weightFoodData;
    }

    const safeActivityIndex = Number(activityIndex);
    if (Number.isNaN(safeActivityIndex) || safeActivityIndex < 0) {
      return false;
    }

    this.activityAgeData = this.feedingJSON["activity-age"][safeActivityIndex];
    if (!this.activityAgeData || !this.activityAgeData["weight-food"]) {
      this.weightFoodData = "";
      return false;
    }

    this.weightFoodData = this.getWeightFoodData(this.activityAgeData, this.dogValues.dogWeight);
    return !!this.weightFoodData;
  }

  getWeightFoodData(activityAgeData, dogWeightValue) {
    const weightFoodRows = activityAgeData?.["weight-food"];
    const dogWeight = Number(dogWeightValue);

    if (!weightFoodRows || !Number.isFinite(dogWeight) || dogWeight <= 0) {
      return null;
    }

    const normalizedRows = Array.isArray(weightFoodRows)
      ? weightFoodRows
      : Object.values(weightFoodRows);
    if (normalizedRows.length === 0) {
      return null;
    }

    const requestedIndex = Math.round(dogWeight) - 1;
    const safeIndex = Math.min(Math.max(requestedIndex, 0), normalizedRows.length - 1);
    const nearestRow = normalizedRows[safeIndex];

    return Number.isFinite(Number(nearestRow?.["kcal-required"]))
      ? nearestRow
      : normalizedRows.find((row) => Number.isFinite(Number(row?.["kcal-required"]))) || null;
  }

  togglePuppyDisclaimer(isVisible) {
    const puppyDisclaimer = this.root.querySelectorAll(".puppy-disclaimer");
    if (puppyDisclaimer.length > 0) {
      puppyDisclaimer[0].style.display = isVisible ? "block" : "none";
    }
  }

  getDogAgeInWeeks(dogDobValue = "") {
    const normalizedDogDob = this.normalizeDogDob(dogDobValue);
    const parsedDob = this.parseDogDob(normalizedDogDob);
    if (!parsedDob) {
      return null;
    }

    const ageInWeeks = Math.floor((this.today - parsedDob) / this.aWeek);
    return Number.isFinite(ageInWeeks) ? ageInWeeks : null;
  }

  toggleUnderEightWeeksWarning(isVisible) {
    if (this.puppyWarnings?.length) {
      this.puppyWarnings.forEach((warning) => {
        warning.classList.toggle("active", isVisible);
      });
    } else if (this.puppywarning) {
      this.puppywarning.classList.toggle("active", isVisible);
    }

    if (this.contactlink) {
      this.contactlink.classList.toggle("active", isVisible);
    }

    if (this.nextButton) {
      this.nextButton.classList.toggle("hide", isVisible);
    }
  }

  getDogAgeLabel(dogAgeValue) {
    const labels = {
      "8 - 52 weeks puppy": "Puppy",
      "1-7 years adult": "Adult",
      "7+ senior": "Senior",
    };
    return labels[dogAgeValue] || "";
  }

  toggleAgeMismatchWarning(isVisible, detailText = "") {
    if (this.ageMismatchDetails?.length) {
      this.ageMismatchDetails.forEach((detail) => {
        detail.textContent = isVisible ? detailText : "";
      });
    }

    if (this.ageMismatchWarnings?.length) {
      this.ageMismatchWarnings.forEach((warning) => {
        warning.classList.toggle("active", isVisible);
      });
    }
  }

  calculatePuppyKcalRequirementForDobAndWeight(dogDobValue, dogWeightValue) {
    const inputAge = this.parseDogDob(this.normalizeDogDob(dogDobValue));
    if (!inputAge) {
      return null;
    }

    const dogWeight = Number(dogWeightValue);
    if (!Number.isFinite(dogWeight) || dogWeight <= 0) {
      return null;
    }

    const diffWeeks = Math.floor((this.today - inputAge) / this.aWeek);
    if (!Number.isFinite(diffWeeks) || diffWeeks < 0) {
      return null;
    }

    // Avoid log(0) for same-day DOB values.
    const lnWeeks = Math.log(Math.max(diffWeeks, 1));
    const weightArray = this.getWeightArray(dogWeight);
    if (
      !Array.isArray(weightArray) ||
      weightArray.length < 2 ||
      !Number.isFinite(weightArray[0]) ||
      !Number.isFinite(weightArray[1])
    ) {
      return null;
    }

    const percentFinalAdult = (weightArray[0] * lnWeeks - weightArray[1]) / 100;
    const effectiveWeight = dogWeight * percentFinalAdult;
    if (
      !Number.isFinite(percentFinalAdult) ||
      !Number.isFinite(effectiveWeight) ||
      effectiveWeight <= 0
    ) {
      return null;
    }

    const kcalRequirement =
      (254.1 - 135 * (effectiveWeight / dogWeight)) * Math.pow(effectiveWeight, 0.75);
    return Number.isFinite(kcalRequirement) ? kcalRequirement : null;
  }

  calculatePuppyKcalRequirement() {
    return this.calculatePuppyKcalRequirementForDobAndWeight(
      this.getCanonicalDogDob() || this.dogDobInput.value,
      this.dogValues.dogWeight,
    );
  }

  calculateTotalKcalRequirement({ showPuppyDisclaimer = false } = {}) {
    if (this.isPuppyAge(this.dogAgeInput.value)) {
      const ageInWeeks = this.getDogAgeInWeeks(
        this.getCanonicalDogDob() || this.dogValues.dogDob || "",
      );
      if (ageInWeeks !== null && ageInWeeks < 8) {
        this.toggleUnderEightWeeksWarning(true);
        return null;
      }

      if (showPuppyDisclaimer) {
        this.togglePuppyDisclaimer(true);
      }

      this.toggleUnderEightWeeksWarning(false);
      const puppyCalculatedKcal = this.calculatePuppyKcalRequirement();
      if (Number.isFinite(puppyCalculatedKcal)) {
        return puppyCalculatedKcal;
      }
      const dogWeight = Number(this.dogValues.dogWeight || this.dogWeightInput?.value);
      const puppyBaseKcal = Number(
        this.getWeightFoodData(this.getPuppyActivityData(), dogWeight)?.["kcal-required"],
      );
      return Number.isFinite(puppyBaseKcal) ? puppyBaseKcal : null;
    }

    const baseKcal = Number(this.weightFoodData?.["kcal-required"]);
    if (!Number.isFinite(baseKcal)) {
      return null;
    }

    this.toggleUnderEightWeeksWarning(false);
    return baseKcal;
  }

  calculateDailyKcalForDogData(dogData = {}) {
    const dogWeight = Number(dogData.dog_weight_current);
    if (!Number.isFinite(dogWeight) || dogWeight <= 0) {
      return null;
    }

    const normalizedDogAge =
      this.normalizeDogAge(dogData.dog_age) || this.deriveDogAgeFromDob(dogData.dog_dob || "");
    if (!normalizedDogAge) {
      return null;
    }

    if (this.isPuppyAge(normalizedDogAge)) {
      const ageInWeeks = this.getDogAgeInWeeks(dogData.dog_dob || "");
      if (ageInWeeks !== null && ageInWeeks < 8) {
        return null;
      }

      const puppyCalculatedKcal = this.calculatePuppyKcalRequirementForDobAndWeight(
        dogData.dog_dob || "",
        dogWeight,
      );
      if (Number.isFinite(puppyCalculatedKcal)) {
        return puppyCalculatedKcal;
      }
      const puppyBaseKcal = Number(
        this.getWeightFoodData(this.getPuppyActivityData(), dogWeight)?.["kcal-required"],
      );
      return Number.isFinite(puppyBaseKcal) ? puppyBaseKcal : null;
    }

    const activityIndex = this.resolveActivityAgeIndex({
      dogAge: normalizedDogAge,
      dogActive: dogData.dog_activity,
    });

    const activityAgeData = this.feedingJSON?.["activity-age"]?.[activityIndex];
    const baseKcal = Number(this.getWeightFoodData(activityAgeData, dogWeight)?.["kcal-required"]);

    if (!Number.isFinite(baseKcal)) {
      return null;
    }

    return baseKcal;
  }

  applyCalculatedFoodValues(totalKcalRequirement) {
    const {
      wetGrams,
      dryGrams,
      wetTrays,
      wetTraysMin,
      wetTraysMax,
      wetGramsMin,
      wetGramsMax,
      dryGramsMin,
      dryGramsMax,
      dailyKcal,
    } = this.updateFoodOptionsDisplay(this.dogValues.dogFoodType, totalKcalRequirement);

    this.dogValues.wetGrams = wetGrams;
    this.dogValues.wetGramsMin = wetGramsMin;
    this.dogValues.wetGramsMax = wetGramsMax;
    this.dogValues.dryGrams = dryGrams;
    this.dogValues.dryGramsMin = dryGramsMin;
    this.dogValues.dryGramsMax = dryGramsMax;
    this.dogValues.wetTrays = wetTrays;
    this.dogValues.wetTraysMin = wetTraysMin;
    this.dogValues.wetTraysMax = wetTraysMax;
    this.dogValues.dailyKcal = dailyKcal;
  }

  handleDogActivityRadios(event) {
    const radioButton = event.target;
    if (radioButton.checked) {
      this.dogValues.dogActive = radioButton.value;

      this.dogActiveElements.forEach((element) => {
        element.textContent = this.dogValues.dogActive;
      });

      this.activityLevel = event.target.id;
      const activityIndex = this.resolveActivityAgeIndex({
        dogAge: this.dogAgeInput.value,
        dogActive: this.dogValues.dogActive,
        activityLevelId: this.activityLevel,
      });
      this.syncActivityAgeAndWeightFoodData(activityIndex);
    }
  }

  handleDogNameChange(event) {
    this.dogNamesValidate(event);
  }

  handleDogBirthdayChange(event) {
    const dobField = event?.target || this.root.querySelector("#dog-dob");
    if (!dobField) return;

    const inputValue = dobField.value || "";
    const normalizedDogDob = this.normalizeDogDob(inputValue);
    const todayStr = new Date().toISOString().split("T")[0];

    if (normalizedDogDob && normalizedDogDob > todayStr) {
      dobField.classList.add("inError");
      dobField.setCustomValidity("Date cannot be in the future.");
      dobField.reportValidity();
      this.setCanonicalDogDob("");
      this.toggleUnderEightWeeksWarning(false);
      this.toggleAgeMismatchWarning(false);
      this.fieldsetWeight.classList.remove("fieldset-puppy");
      return;
    }

    if (!normalizedDogDob && inputValue !== "") {
      dobField.classList.add("inError");
      dobField.setCustomValidity("Please enter a valid date.");
      dobField.reportValidity();
      this.setCanonicalDogDob("");
      this.toggleUnderEightWeeksWarning(false);
      this.toggleAgeMismatchWarning(false);
      this.fieldsetWeight.classList.remove("fieldset-puppy");
      return;
    }

    const previousDogAge =
      this.normalizeDogAge(this.dogAgeInput?.value) || this.normalizeDogAge(this.dogValues.dogAge);
    const canonicalDogDob = this.setCanonicalDogDob(normalizedDogDob);
    this.dogValues.dogDob = canonicalDogDob;
    if (canonicalDogDob) {
      dobField.value = this.formatDogDobForDisplay(canonicalDogDob) || dobField.value;
    }

    dobField.classList.remove("inError");
    dobField.setCustomValidity("");
    dobField.reportValidity();

    const inputAge = this.parseDogDob(canonicalDogDob);
    if (!inputAge) {
      this.toggleUnderEightWeeksWarning(false);
      this.toggleAgeMismatchWarning(false);
      this.fieldsetWeight.classList.remove("fieldset-puppy");
      return;
    }

    const diffWeeks = Math.floor((this.today - inputAge) / this.aWeek);
    if (!Number.isFinite(diffWeeks) || diffWeeks < 0) {
      this.toggleUnderEightWeeksWarning(false);
      this.toggleAgeMismatchWarning(false);
      this.fieldsetWeight.classList.remove("fieldset-puppy");
      return;
    }

    if (diffWeeks >= 364) {
      //senior
      this.dogAgeInput.value = "7+ senior";
    } else if (diffWeeks >= 52) {
      //adult
      this.dogAgeInput.value = "1-7 years adult";
    } else {
      //puppy
      this.dogAgeInput.value = "8 - 52 weeks puppy";
    }

    this.dogValues.dogAge = this.dogAgeInput.value;
    this.toggleNeuteredOptionsVisibility(this.dogValues.dogAge);
    this.toggleDryPreferenceOptionsVisibility(this.dogValues.dogAge);
    this.toggleDogWeightLabel(this.dogValues.dogAge);
    this.dogAgeInput.dispatchEvent(new Event("change"));

    if (previousDogAge && previousDogAge !== this.dogValues.dogAge) {
      const detailText = `${this.getDogAgeLabel(previousDogAge)} → ${this.getDogAgeLabel(this.dogValues.dogAge)}`;
      this.toggleAgeMismatchWarning(true, detailText);
    } else {
      this.toggleAgeMismatchWarning(false);
    }

    if (diffWeeks < 8) {
      this.toggleUnderEightWeeksWarning(true);
      this.fieldsetWeight.classList.add("fieldset-puppy");
    } else if (diffWeeks <= 52) {
      this.toggleUnderEightWeeksWarning(false);
      this.fieldsetWeight.classList.add("fieldset-puppy");
    } else {
      this.toggleUnderEightWeeksWarning(false);
      this.fieldsetWeight.classList.remove("fieldset-puppy");
    }
  }

  handleDogSiblingChange() {
    const target = this.dogSiblingsInput;
    if (!target) {
      return;
    }

    const value = parseFloat(target.value);
    if (value < 0) {
      target.classList.add("inError");
      target.setCustomValidity("Value cannot be negative.");
    } else {
      target.classList.remove("inError");
      target.setCustomValidity("");
    }

    target.reportValidity();
  }

  handleDogWeightChange(event) {
    event.target.value = event.target.value.replace(/[^0-9]/g, "");
  }

  updateButtonsAndCounter() {
    const visibleFieldsetIndexes = this.getVisibleFieldsetIndexes();
    const lastVisibleIndex =
      visibleFieldsetIndexes[visibleFieldsetIndexes.length - 1] ?? this.fieldsets.length - 1;

    this.nextButton.style.display = this.currentStep === lastVisibleIndex ? "none" : "inline";
    this.updateFooterResultActions();
    this.stepsCounter.textContent = this.getCurrentVisibleStepNumber();
  }

  showStep(stepIndex) {
    const directionClass = stepIndex < this.currentStep ? "fmf-step-back" : "fmf-step-forward";
    this.fmfModal?.classList.remove("fmf-step-forward", "fmf-step-back");
    this.fmfModal?.classList.add(directionClass);

    this.fieldsets.forEach((fieldset, index) => {
      fieldset.classList.toggle("active", index === stepIndex);
    });
    this.currentStep = stepIndex;
    const currentFieldset = this.fieldsets[this.currentStep];
    this.fmfModal?.classList.toggle(
      "fmf-results-active",
      Boolean(
        currentFieldset?.classList.contains("fieldset-summary") ||
          currentFieldset?.dataset.step === "10",
      ),
    );
    this.updateProgressBar();
  }

  updateProgressBar() {
    const visibleFieldsetIndexes = this.getVisibleFieldsetIndexes();
    const currentVisibleStepNumber = this.getCurrentVisibleStepNumber();
    this.progressBar.style.width =
      (currentVisibleStepNumber / visibleFieldsetIndexes.length) * 100 + "%";
  }

  updateUI() {
    this.updateButtonsAndCounter();
    this.updateProgressBar();
    const visibleFieldsetIndexes = this.getVisibleFieldsetIndexes();
    this.stepsTotal.textContent = visibleFieldsetIndexes.length;
    this.currentStepElement.textContent = this.getCurrentVisibleStepNumber();
  }

  shouldIncludeDryPreferenceStep() {
    return (
      this.dogValues.dogFoodType === "dog-feeding-dry-only" ||
      this.dogValues.dogFoodType === "dog-feeding-mix"
    );
  }

  shouldSkipFieldset(fieldset) {
    const step = fieldset?.dataset?.step;
    return step === "6" && !this.shouldIncludeDryPreferenceStep();
  }

  getVisibleFieldsetIndexes() {
    return Array.from(this.fieldsets)
      .map((fieldset, index) => ({ fieldset, index }))
      .filter(({ fieldset }) => !this.shouldSkipFieldset(fieldset))
      .map(({ index }) => index);
  }

  getCurrentVisibleStepNumber() {
    const visibleFieldsetIndexes = this.getVisibleFieldsetIndexes();
    const visiblePosition = visibleFieldsetIndexes.indexOf(this.currentStep);
    return visiblePosition >= 0 ? visiblePosition + 1 : 1;
  }

  getAdjacentStepIndex(direction) {
    let nextIndex = this.currentStep + direction;
    while (nextIndex >= 0 && nextIndex < this.fieldsets.length) {
      const nextFieldset = this.fieldsets[nextIndex];
      if (!this.shouldSkipFieldset(nextFieldset)) {
        return nextIndex;
      }
      nextIndex += direction;
    }
    return this.currentStep;
  }

  initialStorageSetup() {
    // Initial setup
    const fmfdata = this.getStoredJSON("FMF");
    if (!fmfdata) return;
    if (!fmfdata.dog_name) return;

    this.dogValues.dogId = this.dogIdInput.value = fmfdata.id;
    this.dogValues.dogName = this.dogNameInput.value = fmfdata.dog_name;
    this.dogImage.src = fmfdata.dog_image;
    const normalizedStoredDogDob = this.normalizeDogDob(fmfdata.dog_dob);
    const displayStoredDogDob = this.formatDogDobForDisplay(
      normalizedStoredDogDob || fmfdata.dog_dob,
    );
    this.dogValues.dogDob = normalizedStoredDogDob;
    this.dogDobInput.value = displayStoredDogDob || this.dogDobInput.value;
    this.setCanonicalDogDob(normalizedStoredDogDob);

    if (fmfdata.dog_breed && fmfdata.dog_breed !== "no_answer") {
      // Handle stored breed strings across formats: "a / b", "a_/_b", etc.
      const storedBreeds = String(fmfdata.dog_breed)
        .split("/")
        .map((breed) => breed.replaceAll("_", " ").trim())
        .filter((breed) => breed !== "");

      const isUnknownBreed =
        storedBreeds.length === 1 &&
        (storedBreeds[0].toLowerCase() === "unknown" ||
          storedBreeds[0].toLowerCase() === "i don't know");

      const unknownBreedInput = this.root.querySelector("#unknownBreed");
      if (unknownBreedInput) {
        unknownBreedInput.checked = isUnknownBreed;
        unknownBreedInput.dispatchEvent(new Event("change"));
      } else {
        // Trigger breed selection change first (it clears fields), then repopulate.
        const checkedBreedType = this.root.querySelector('input[name="breedSelection"]:checked');
        if (checkedBreedType) {
          checkedBreedType.dispatchEvent(new Event("change"));
        }
      }

      if (!isUnknownBreed) {
        const singleInput = this.root.querySelector(".single-breed-container input");
        if (singleInput) {
          // Cross-breed selection is removed; restore only the first saved breed.
          singleInput.value = storedBreeds[0] || "";
        }
      }

      this.updateDogBreedValue();
    }
    const normalizedStoredDogAge = this.normalizeDogAge(fmfdata.dog_age);
    const derivedDogAgeFromDob = this.deriveDogAgeFromDob(this.dogValues.dogDob);
    const safeDogAge = normalizedStoredDogAge || derivedDogAgeFromDob;
    this.dogAgeInput.value = safeDogAge;
    this.dogValues.dogAge = safeDogAge;
    this.toggleNeuteredOptionsVisibility(safeDogAge);
    this.toggleDryPreferenceOptionsVisibility(safeDogAge);
    this.toggleDogWeightLabel(safeDogAge);
    const storedDogAgeInWeeks = this.getDogAgeInWeeks(this.dogValues.dogDob);
    this.toggleUnderEightWeeksWarning(storedDogAgeInWeeks !== null && storedDogAgeInWeeks < 8);
    this.dogValues.dogFoodType = fmfdata.dog_preferences;
    this.dogValues.dogDryFoodPreference =
      fmfdata.dog_dry_preference || fmfdata.dog_dry_preferences || "";
    this.dogValues.wetGrams = fmfdata.dog_wet_grams;
    this.dogValues.wetGramsMin = fmfdata.dog_wet_grams_min || fmfdata.dog_wet_grams;
    this.dogValues.wetGramsMax = fmfdata.dog_wet_grams_max || fmfdata.dog_wet_grams;
    this.dogValues.dryGrams = fmfdata.dog_dry_grams;
    this.dogValues.dryGramsMin = fmfdata.dog_dry_grams_min || fmfdata.dog_dry_grams;
    this.dogValues.dryGramsMax = fmfdata.dog_dry_grams_max || fmfdata.dog_dry_grams;
    this.dogValues.wetTrays = fmfdata.dog_wet_trays;
    this.dogValues.wetTraysMin = fmfdata.dog_wet_trays_min || fmfdata.dog_wet_trays;
    this.dogValues.wetTraysMax = fmfdata.dog_wet_trays_max || fmfdata.dog_wet_trays;
    const storedDailyKcal = Number(fmfdata.dog_daily_kcal);
    const calculatedDailyKcal = this.calculateDailyKcalForDogData(fmfdata);
    this.dogValues.dailyKcal = Number.isFinite(storedDailyKcal)
      ? storedDailyKcal
      : Number.isFinite(calculatedDailyKcal)
        ? Math.round(calculatedDailyKcal)
        : 0;

    this.dogGenderInput.forEach((radioButton) => {
      if (radioButton.value === fmfdata.dog_gender) {
        radioButton.checked = true;
        this.dogValues.dogGender = fmfdata.dog_gender;
      }
    });

    if (!this.isPuppyAge(safeDogAge)) {
      this.dogNeuteredInput.forEach((radioButton) => {
        if (radioButton.value === fmfdata.dog_neutered) {
          radioButton.checked = true;
          this.dogValues.dogNeutered = fmfdata.dog_neutered;
        }
      });
    }

    this.dogRescueInput.forEach((radioButton) => {
      if (radioButton.value === fmfdata.dog_rescue) {
        radioButton.checked = true;
        this.dogValues.dogRescue = fmfdata.dog_rescue;
      }
    });

    this.dogValues.dogSiblings = this.dogSiblingsInput.value = fmfdata.dog_siblings;
    this.dogValues.dogBio = this.dogBioInput.value =
      fmfdata.dog_bio !== "no_answer" ? fmfdata.dog_bio : this.dogBioInput.value;
    this.dogValues.dogTiktok = this.dogTiktokInput.value =
      fmfdata.dog_tiktok !== "no_answer" ? fmfdata.dog_tiktok : this.dogTiktokInput.value;
    this.dogValues.dogInstagram = this.dogInstagramInput.value =
      fmfdata.dog_instagram !== "no_answer" ? fmfdata.dog_instagram : this.dogInstagramInput.value;

    this.dogValues.dogWeight = this.dogWeightInput.value = fmfdata.dog_weight_current;

    this.dogActiveInput.forEach((radioButton) => {
      if (radioButton.value === fmfdata.dog_activity) {
        radioButton.checked = true;
        this.dogValues.dogActive = fmfdata.dog_activity;
      }
    });

    let selectedFavourites = [];
    const dogFavourites = fmfdata.dog_favourites;

    // Reset defaults before applying saved favourites from storage.
    this.dogFavouritesCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    if (typeof dogFavourites === "string") {
      const normalized = dogFavourites.trim().toLowerCase();
      if (normalized === "everything") {
        selectedFavourites = Array.from(this.dogFavouritesCheckboxes).map((checkbox) =>
          String(checkbox.value).trim().toLowerCase(),
        );
      } else if (normalized && normalized !== "all recipes") {
        selectedFavourites = dogFavourites
          .split("|")
          .map((value) => String(value).trim().toLowerCase())
          .filter(Boolean);
      }
    } else if (Array.isArray(dogFavourites)) {
      selectedFavourites = dogFavourites
        .map((value) => String(value).trim().toLowerCase())
        .filter((value) => value && value.toLowerCase() !== "all recipes");
    }

    selectedFavourites = selectedFavourites.filter((favourite) => {
      const checkboxElement = Array.from(this.dogFavouritesCheckboxes).find((checkbox) => {
        return String(checkbox.value).trim().toLowerCase() === favourite;
      });
      if (!checkboxElement) {
        return false;
      }

      checkboxElement.checked = true;
      return true;
    });

    this.dogValues.dogFavourites = selectedFavourites.length ? selectedFavourites.join("|") : "";

    const selectedFavouritesString =
      selectedFavourites.length > 0
        ? selectedFavourites.length > 1
          ? `${selectedFavourites.slice(0, -1).join(", ")} and ${selectedFavourites[selectedFavourites.length - 1]}`
          : selectedFavourites[0]
        : "everything";

    this.dogFavouritesElements.forEach(
      (element) => (element.textContent = selectedFavouritesString),
    );

    if (this.dogValues.dogFoodType) {
      const foodTypeElement = this.root.querySelector(`#${this.dogValues.dogFoodType}`);
      if (foodTypeElement) foodTypeElement.checked = true;
    }

    if (this.dogValues.dogDryFoodPreference) {
      const isPuppyAge = this.isPuppyAge(this.dogValues.dogAge);
      const isLightlyBakedPreference =
        this.dogValues.dogDryFoodPreference === "dog-dry-pref-lightly-baked" ||
        this.dogValues.dogDryFoodPreference === "lightly_baked";
      if (isPuppyAge && isLightlyBakedPreference) {
        this.dogValues.dogDryFoodPreference = "";
      } else {
        const dryPreferenceElement = this.root.querySelector(
          `#${this.dogValues.dogDryFoodPreference}`,
        );
        if (dryPreferenceElement) dryPreferenceElement.checked = true;
      }
    }

    const activityIndex = this.resolveActivityAgeIndex({
      dogAge: this.dogValues.dogAge,
      dogActive: this.dogValues.dogActive,
    });
    const hasActivityData = this.syncActivityAgeAndWeightFoodData(activityIndex);
    if (!hasActivityData) {
      return;
    }

    const totalKcalRequirement = this.calculateTotalKcalRequirement({ showPuppyDisclaimer: true });
    if (totalKcalRequirement === null) {
      return;
    }

    this.updateFoodOptionsDisplay(this.dogValues.dogFoodType, totalKcalRequirement);

    const allFieldsets = Array.from(this.fieldsets || []);
    const summaryIndex = allFieldsets.findIndex((fieldset) =>
      fieldset.classList.contains("fieldset-summary"),
    );
    const resolvedSummaryIndex =
      summaryIndex >= 0 ? summaryIndex : Math.max(allFieldsets.length - 1, 0);
    const preSummaryIndex = Math.max(resolvedSummaryIndex - 1, 0);

    this.skipFormSubmissionOnLoad = true;
    this.showStep(preSummaryIndex);
    this.updateUI();

    if (resolvedSummaryIndex > preSummaryIndex && this.nextButton) {
      this.nextButton.click();
    }

    this.skipFormSubmissionOnLoad = false;
  }

  openTab(evt, tabName) {
    this.root.querySelectorAll(".tab-content").forEach((tabContent) => {
      tabContent.classList.remove("active");
    });

    this.root.querySelectorAll(".tab-link").forEach((tabLink) => {
      tabLink.classList.remove("active");
    });

    this.root.querySelector(`#${tabName}`)?.classList.add("active");
    evt.currentTarget.classList.add("active");

    const dogId = this.root.querySelector(`#${tabName}`)?.dataset.dogId;
    const dogData = this.getStoredPassportDogs();
    const activeData = dogData.find((obj) => this.idsMatch(obj.id, dogId));

    if (activeData) {
      this.syncFormStateToDog(activeData);
      this.persistFMFData(activeData);
      if (window.QF?.updateRecommendationContextFromDogData) {
        window.QF.updateRecommendationContextFromDogData(activeData);
      }
      if (window.QF?.renderProductCardFeedingAmounts) {
        window.QF.renderProductCardFeedingAmounts(document);
      }
    }

    this.generateCollectionUrl(activeData);
  }

  syncFormStateToDog(dogData) {
    if (!dogData) {
      return;
    }

    this.dogValues.dogId = dogData.id ?? this.dogValues.dogId;
    this.dogValues.dogName = dogData.dog_name || "";
    this.dogValues.dogWeight = dogData.dog_weight_current || "";
    this.dogValues.dogDob = this.normalizeDogDob(dogData.dog_dob || "");
    this.dogValues.dogAge =
      this.normalizeDogAge(dogData.dog_age) || this.deriveDogAgeFromDob(this.dogValues.dogDob);
    this.dogValues.dogFoodType = dogData.dog_preferences || "";
    this.dogValues.dogDryFoodPreference =
      dogData.dog_dry_preference || dogData.dog_dry_preferences || "";
    this.dogValues.dogActive = dogData.dog_activity || "";
    this.dogValues.dogGender = dogData.dog_gender || "";
    this.dogValues.dogNeutered = dogData.dog_neutered || "";
    this.dogValues.dogRescue = dogData.dog_rescue || "";
    this.dogValues.dogSiblings = dogData.dog_siblings ?? "";
    this.dogValues.dogBio =
      dogData.dog_bio && dogData.dog_bio !== "no_answer" ? dogData.dog_bio : "";
    this.dogValues.dogInstagram =
      dogData.dog_instagram && dogData.dog_instagram !== "no_answer" ? dogData.dog_instagram : "";
    this.dogValues.dogTiktok =
      dogData.dog_tiktok && dogData.dog_tiktok !== "no_answer" ? dogData.dog_tiktok : "";

    if (this.dogIdInput) {
      this.dogIdInput.value = this.dogValues.dogId;
    }
    if (this.dogNameInput) {
      this.dogNameInput.value = this.dogValues.dogName;
    }
    if (this.dogWeightInput) {
      this.dogWeightInput.value = this.dogValues.dogWeight;
    }
    if (this.dogAgeInput) {
      this.dogAgeInput.value = this.dogValues.dogAge || this.dogAgeInput.value;
    }
    this.toggleNeuteredOptionsVisibility(this.dogAgeInput?.value || this.dogValues.dogAge);
    this.toggleDryPreferenceOptionsVisibility(this.dogAgeInput?.value || this.dogValues.dogAge);
    this.toggleDogWeightLabel(this.dogAgeInput?.value || this.dogValues.dogAge);
    if (this.dogDobInput) {
      this.dogDobInput.value = this.formatDogDobForDisplay(this.dogValues.dogDob) || "";
    }
    this.setCanonicalDogDob(this.dogValues.dogDob);

    if (this.dogImage) {
      this.dogImage.src = dogData.dog_image || this.passportImgSrc;
    }

    if (this.dogSiblingsInput) {
      this.dogSiblingsInput.value = this.dogValues.dogSiblings;
    }
    if (this.dogBioInput) {
      this.dogBioInput.value = this.dogValues.dogBio;
    }
    if (this.dogInstagramInput) {
      this.dogInstagramInput.value = this.dogValues.dogInstagram;
    }
    if (this.dogTiktokInput) {
      this.dogTiktokInput.value = this.dogValues.dogTiktok;
    }

    this.root.querySelectorAll('input[name="dogs[0][dog_preferences]"]').forEach((radio) => {
      radio.checked = radio.id === this.dogValues.dogFoodType;
    });
    this.root.querySelectorAll('input[name="dogs[0][dog_dry_preference]"]').forEach((radio) => {
      if (
        this.isPuppyAge(this.dogValues.dogAge) &&
        (radio.id === "dog-dry-pref-lightly-baked" || radio.value === "lightly_baked")
      ) {
        radio.checked = false;
        return;
      }
      radio.checked =
        radio.id === this.dogValues.dogDryFoodPreference ||
        radio.value === this.dogValues.dogDryFoodPreference;
    });
    this.dogActiveInput.forEach((radio) => {
      radio.checked = radio.value === this.dogValues.dogActive;
    });
    this.dogGenderInput.forEach((radio) => {
      radio.checked = radio.value === this.dogValues.dogGender;
    });
    this.dogNeuteredInput.forEach((radio) => {
      radio.checked = radio.value === this.dogValues.dogNeutered;
    });
    this.dogRescueInput.forEach((radio) => {
      radio.checked = radio.value === this.dogValues.dogRescue;
    });

    const favourites =
      typeof dogData.dog_favourites === "string" ? dogData.dog_favourites.toLowerCase() : "";
    const selectedFavourites =
      favourites && favourites !== "all recipes"
        ? favourites === "everything"
          ? Array.from(this.dogFavouritesCheckboxes).map((checkbox) =>
              String(checkbox.value).toLowerCase(),
            )
          : favourites
              .split("|")
              .map((value) => value.trim())
              .filter(Boolean)
        : [];
    this.dogFavouritesCheckboxes.forEach((checkbox) => {
      checkbox.checked = selectedFavourites.includes(String(checkbox.value).toLowerCase());
    });

    // Restore a single-breed value in the editable input.
    const singleBreedInput = this.root.querySelector(".single-breed-container .breed-input");
    if (singleBreedInput) {
      const formattedBreed =
        String(dogData.dog_breed || "")
          .replaceAll("_", " ")
          .split("/")
          .map((value) => value.trim())
          .filter(Boolean)[0] || "";
      singleBreedInput.value =
        formattedBreed && formattedBreed !== "no answer" ? formattedBreed : "";
      this.updateDogBreedValue();
    }

    this.root.querySelectorAll(".fmf-form form [fmf-form-dog-name]").forEach((element) => {
      element.textContent = this.dogValues.dogName || "";
    });
  }

  bindTabs() {
    if (this.tabsBound) return;

    const tabsContainer = this.root.querySelector(".tabs");
    if (!tabsContainer) return;

    tabsContainer.addEventListener("click", (event) => {
      const tabButton = event.target.closest("[data-change-tab]");
      if (!tabButton) return;

      event.preventDefault();
      const tabName = tabButton.getAttribute("data-tab-name");
      if (!tabName) return;

      this.openTab({ currentTarget: tabButton }, tabName);
    });

    this.tabsBound = true;
  }

  bindAccountTabs() {
    const registerTab = this.root.querySelector(".register-tab");
    const loginTab = this.root.querySelector(".login-tab");
    const registerTabContent = this.root.querySelector(".register-tab-content");
    const loginTabContent = this.root.querySelector(".login-tab-content");

    if (registerTab) {
      registerTab.addEventListener("click", (event) => {
        event.preventDefault();
        registerTab.querySelector(".account-tab-link").classList.add("active");
        loginTab.querySelector(".account-tab-link").classList.remove("active");
        registerTabContent.classList.remove("hidden");
        loginTabContent.classList.add("hidden");
      });
    }

    if (loginTab) {
      loginTab.addEventListener("click", (event) => {
        event.preventDefault();
        loginTab.querySelector(".account-tab-link").classList.add("active");
        registerTab.querySelector(".account-tab-link").classList.remove("active");
        loginTabContent.classList.remove("hidden");
        registerTabContent.classList.add("hidden");
      });
    }
  }

  createNewTab() {
    const tabsContainer = this.root.querySelector(".tabs");
    if (!tabsContainer) {
      console.error("Tabs container not found");
      return null;
    }

    const tabList = tabsContainer.querySelector("ul");
    if (!tabList) {
      return null;
    }

    const tabContents = tabsContainer.querySelectorAll(".tab-content");
    const newTabId = `tab-${tabContents.length + 1}`;

    const template = this.root.querySelector("#fmf-tab-template");
    if (!template) {
      return null;
    }
    const newForm = template.content.cloneNode(true);

    const newTabLink = document.createElement("li");
    newTabLink.className = "tab";

    const newTabButton = document.createElement("button");
    newTabButton.className = "tab-link";
    newTabButton.setAttribute("data-change-tab", "");
    newTabButton.setAttribute("data-tab-name", newTabId);

    const dogNameSpan = document.createElement("span");
    dogNameSpan.setAttribute("fmf-form-dog-name", "");
    dogNameSpan.setAttribute("data-dog", "dog_name");
    dogNameSpan.textContent = "Name";
    newTabButton.appendChild(dogNameSpan);
    newTabLink.appendChild(newTabButton);

    const newTab = document.createElement("div");
    newTab.id = newTabId;
    newTab.classList.add("tab-content");
    newTab.appendChild(newForm);

    const addNewTabLink = tabList.querySelector(".tab-last");
    tabList.insertBefore(newTabLink, addNewTabLink || null);
    tabsContainer.appendChild(newTab);

    return { tabId: newTabId, tabLink: newTabLink, tabContent: newTab };
  }

  removeTabByIndex(tabIndex) {
    const tabName = `tab-${tabIndex}`;
    const tabButton = this.root.querySelector(`[data-change-tab][data-tab-name="${tabName}"]`);
    tabButton?.closest("li")?.remove();
    this.root.querySelector(`#${tabName}`)?.remove();
  }

  formatTrayDisplay(trayValue) {
    const parsedTrayValue = Number(trayValue);
    if (Number.isNaN(parsedTrayValue)) {
      return trayValue || "";
    }

    const trayResult = (parsedTrayValue * 100) % 100;
    const wholeTrayCount = Math.floor(parsedTrayValue);

    if (trayResult > 0 && trayResult <= 25) {
      return wholeTrayCount > 0 ? `${wholeTrayCount} 1/4` : "1/4";
    }

    if (trayResult > 25 && trayResult <= 50) {
      return wholeTrayCount > 0 ? `${wholeTrayCount} 1/2` : "1/2";
    }

    if (trayResult > 50 && trayResult <= 95) {
      return wholeTrayCount > 0 ? `${wholeTrayCount} 3/4` : "3/4";
    }

    if (trayResult > 95) {
      return `${wholeTrayCount + 1}`;
    }

    return `${wholeTrayCount}`;
  }

  formatGramsWithAverageAndRange(averageGrams, minGrams, maxGrams) {
    const roundedAverage = Math.round(Number(averageGrams));
    const roundedMin = Math.round(Number(minGrams));
    const roundedMax = Math.round(Number(maxGrams));
    const hasAverage = Number.isFinite(roundedAverage);
    const hasRange = Number.isFinite(roundedMin) && Number.isFinite(roundedMax);

    if (hasAverage && hasRange && roundedMin !== roundedMax) {
      return `${roundedAverage}g (${roundedMin}-${roundedMax}g)`;
    }

    if (hasAverage) {
      return `${roundedAverage}g`;
    }

    if (hasRange) {
      return roundedMin === roundedMax ? `${roundedMin}g` : `${roundedMin}-${roundedMax}g`;
    }

    return "0g";
  }

  hasDistinctGramsRange(minGrams, maxGrams) {
    const roundedMin = Math.round(Number(minGrams));
    const roundedMax = Math.round(Number(maxGrams));
    return Number.isFinite(roundedMin) && Number.isFinite(roundedMax) && roundedMin !== roundedMax;
  }

  toggleRangeDisclaimer(disclaimerElement, shouldShow) {
    if (!disclaimerElement) {
      return;
    }

    disclaimerElement.style.display = shouldShow ? "block" : "none";
  }

  setTabFoodSummaryDisplay(tabContent, dogPreference) {
    const wetFoodSummary = tabContent.querySelector("[fmf-form-summary-wet-food]");
    const dryFoodSummary = tabContent.querySelector("[fmf-form-summary-dry-food]");
    const andElement = tabContent.querySelector(".and");
    if (!wetFoodSummary || !dryFoodSummary || !andElement) {
      return;
    }

    const displayFoodSummary = (showWet, showDry, showAnd) => {
      wetFoodSummary.style.display = showWet ? "flex" : "none";
      dryFoodSummary.style.display = showDry ? "flex" : "none";
      andElement.style.display = showAnd ? "flex" : "none";
    };

    switch (dogPreference) {
      case "dog-feeding-wet-only":
        displayFoodSummary(true, false, false);
        break;
      case "dog-feeding-dry-only":
        displayFoodSummary(false, true, false);
        break;
      case "dog-feeding-mix":
        displayFoodSummary(true, true, true);
        break;
      default:
        displayFoodSummary(true, true, true);
    }
  }

  populateSummaryTab(tabContent, dogData) {
    if (!tabContent || !dogData) {
      return;
    }

    const dogNames = tabContent.querySelectorAll("[fmf-form-dog-name]");
    const dogDob = tabContent.querySelector("[fmf-form-dog-age]");
    const dogFavourites = tabContent.querySelector("[fmf-form-dog-flavours]");
    const dogWeight = tabContent.querySelector("[fmf-form-dog-weight]");
    const dogWeightLabel = tabContent.querySelector("[fmf-form-dog-weight-label]");
    const dogActive = tabContent.querySelector("[fmf-form-dog-active]");
    const recommendationsLink = tabContent.querySelector("[fmf-form-recommendations-link]");
    const dogId = tabContent.querySelector("[fmf-form-dog-id]");
    const dogWetGrams = tabContent.querySelector("[fmf-form-wet-daily]");
    const dogDryGrams = tabContent.querySelector("[fmf-form-dry-daily]");
    const dogWetTrays = tabContent.querySelector("[fmf-tray-wet-daily]");
    const dogDailyKcal = tabContent.querySelector("[fmf-form-kcal-daily]");
    const rangeDisclaimer = tabContent.querySelector("[fmf-form-range-disclaimer]");

    if (recommendationsLink) {
      this.generateCollectionUrl(dogData);
      recommendationsLink.href = this.RecommendedCollectionURL;
    }

    if (dogId) {
      const baseHref = dogId.dataset.baseHref || dogId.getAttribute("href") || "";
      if (!dogId.dataset.baseHref) {
        dogId.dataset.baseHref = baseHref;
      }
      const joinChar = baseHref.includes("?") ? "&" : "?";
      dogId.href = `${baseHref}${joinChar}dogId=${encodeURIComponent(dogData.id)}`;
      tabContent.dataset.dogId = dogData.id;
    }

    if (dogWeight) {
      dogWeight.textContent = dogData.dog_weight_current || "no_answer";
    }

    const isPuppy = dogData.dog_age === "8 - 52 weeks puppy";
    if (dogWeightLabel) {
      dogWeightLabel.textContent = isPuppy ? "is expected to weigh" : "weighs";
    }

    if (dogActive) {
      dogActive.textContent = String(dogData.dog_activity || "").replaceAll("_", " ");
    }

    dogNames.forEach((element) => {
      element.textContent = dogData.dog_name || "Name";
    });

    const dogWetGramsMin = dogData.dog_wet_grams_min ?? dogData.dog_wet_grams;
    const dogWetGramsMax = dogData.dog_wet_grams_max ?? dogData.dog_wet_grams;
    if (dogWetGrams) {
      dogWetGrams.textContent = this.formatGramsWithAverageAndRange(
        dogData.dog_wet_grams,
        dogWetGramsMin,
        dogWetGramsMax,
      );
    }

    const dogDryGramsMin = dogData.dog_dry_grams_min ?? dogData.dog_dry_grams;
    const dogDryGramsMax = dogData.dog_dry_grams_max ?? dogData.dog_dry_grams;
    if (dogDryGrams) {
      dogDryGrams.textContent = this.formatGramsWithAverageAndRange(
        dogData.dog_dry_grams,
        dogDryGramsMin,
        dogDryGramsMax,
      );
    }

    const dogWetTraysMin = dogData.dog_wet_trays_min ?? dogData.dog_wet_trays;
    const dogWetTraysMax = dogData.dog_wet_trays_max ?? dogData.dog_wet_trays;
    const dogWetTraysMinDisplay = this.formatTrayDisplay(dogWetTraysMin);
    const dogWetTraysMaxDisplay = this.formatTrayDisplay(dogWetTraysMax);
    if (dogWetTrays) {
      dogWetTrays.textContent =
        dogWetTraysMinDisplay === dogWetTraysMaxDisplay
          ? dogWetTraysMinDisplay
          : `${dogWetTraysMinDisplay} - ${dogWetTraysMaxDisplay}`;
    }

    if (dogFavourites) {
      dogFavourites.textContent = this.getSelectedFavourites(dogData.dog_favourites);
    }

    if (dogDailyKcal) {
      const calculatedDailyKcal = this.calculateDailyKcalForDogData(dogData);
      const dailyKcalValue = Number.isFinite(calculatedDailyKcal)
        ? Math.round(calculatedDailyKcal)
        : 0;
      dogDailyKcal.textContent = dailyKcalValue;
      const dailyKcalRow = dogDailyKcal.closest(".result-kcal");
      if (dailyKcalRow) {
        dailyKcalRow.style.display = dailyKcalValue > 0 ? "" : "none";
      }
    }

    this.toggleRangeDisclaimer(rangeDisclaimer, true);

    if (dogDob) {
      const dogAgeValue = dogData.dog_age;
      let newDogAgeValue = this.getNewDogAgeValue(dogAgeValue, dogData.dog_dob);
      if (!newDogAgeValue && dogAgeValue === "8 - 52 weeks puppy") {
        newDogAgeValue = "a puppy";
      }
      dogDob.textContent = newDogAgeValue;
    }

    this.setTabFoodSummaryDisplay(tabContent, dogData.dog_preferences);
  }

  populateTabForms() {
    const tabsContainer = this.root.querySelector(".tabs");
    if (!tabsContainer) {
      return;
    }
    const tabList = tabsContainer.querySelector("ul");
    if (!tabList) {
      return;
    }

    const dogs = this.getStoredPassportDogs();
    const targetTabCount = dogs.length;
    const previouslyActiveTabId = tabsContainer.querySelector(".tab-content.active")?.id;

    let currentTabCount = tabsContainer.querySelectorAll(".tab-content").length;
    while (currentTabCount < targetTabCount) {
      const createdTab = this.createNewTab();
      if (!createdTab) {
        break;
      }
      currentTabCount = tabsContainer.querySelectorAll(".tab-content").length;
    }

    while (currentTabCount > targetTabCount && currentTabCount > 1) {
      this.removeTabByIndex(currentTabCount);
      currentTabCount -= 1;
    }

    const tabButtons = Array.from(tabList.querySelectorAll("[data-change-tab]"));
    const tabContents = Array.from(tabsContainer.querySelectorAll(".tab-content"));

    tabButtons.forEach((tabButton, index) => {
      const tabItem = tabButton.closest("li");
      tabButton.classList.remove("active");
      if (tabItem) {
        tabItem.classList.toggle("hidden", index >= targetTabCount);
      }
    });

    tabContents.forEach((tabContent, index) => {
      tabContent.classList.remove("active");
      tabContent.classList.toggle("hidden", index >= targetTabCount);
    });

    dogs.forEach((dogData, index) => {
      const tabName = `tab-${index + 1}`;
      const tabButton = tabList.querySelector(`[data-change-tab][data-tab-name="${tabName}"]`);
      const tabContent = tabsContainer.querySelector(`#${tabName}`);

      if (tabButton) {
        const nameElement = tabButton.querySelector('span[data-dog="dog_name"]');
        if (nameElement) {
          nameElement.textContent = dogData.dog_name || "Name";
        }
      }

      if (tabContent) {
        this.populateSummaryTab(tabContent, dogData);
      }
    });

    if (targetTabCount > 0) {
      const storedFMFData = this.getStoredJSON("FMF");
      const hadStoredFMFData = Boolean(storedFMFData);
      const currentDogTabIndex = dogs.findIndex((dog) =>
        this.idsMatch(dog?.id, this.dogValues.dogId),
      );
      const currentDogTabName = currentDogTabIndex >= 0 ? `tab-${currentDogTabIndex + 1}` : null;
      const canReusePreviousTab = Boolean(
        storedFMFData &&
          previouslyActiveTabId &&
          tabsContainer.querySelector(`#${previouslyActiveTabId}`) &&
          !tabsContainer.querySelector(`#${previouslyActiveTabId}`)?.classList.contains("hidden"),
      );
      const activeTabName =
        currentDogTabName || (canReusePreviousTab ? previouslyActiveTabId : "tab-1");
      const activeTabIndex = Number(String(activeTabName).replace("tab-", "")) - 1;
      const activeDogData = dogs[activeTabIndex] || dogs[0];

      tabList
        .querySelector(`[data-change-tab][data-tab-name="${activeTabName}"]`)
        ?.classList.add("active");
      tabsContainer.querySelector(`#${activeTabName}`)?.classList.add("active");
      if (!hadStoredFMFData && activeDogData) {
        this.syncFormStateToDog(activeDogData);
        this.persistFMFData(activeDogData);
      }
      this.generateCollectionUrl(activeDogData);

      tabList.scrollLeft = hadStoredFMFData ? tabList.scrollLeft : 0;
    }
  }

  handleCloseModal() {
    document.body.classList.remove("fixed");
    this.fmfModal.classList.remove("active");
    this.fmfModalTab.innerHTML = "Feeding Advice";
    window.dispatchEvent(new CustomEvent("find_my_food", { detail: this.dogValuesToObject() }));
  }

  handlePreviousStep() {
    if (this.warning.classList.contains("active")) {
      this.warning.classList.remove("active");
      this.complementaryLink.classList.remove("active");
      this.nextButton.classList.remove("hide");
      this.topperRadio.checked = false;
    }

    if (this.puppywarning?.classList.contains("active")) {
      this.toggleUnderEightWeeksWarning(false);
      this.contactlink.classList.remove("active");
      this.nextButton.classList.remove("hide");
      this.dogBirthdayInput.value = null;
    }

    const previousStepIndex = this.getAdjacentStepIndex(-1);
    this.showStep(previousStepIndex);
    this.updateUI();

    if (this.emailMessage) {
      this.emailMessage.innerHTML = "";
    }
  }

  handlePrevButtonClick() {
    this.formErrorsElement.textContent = "";

    if (this.currentStep === 0) {
      this.handleCloseModal();
    }

    if (this.currentStep > 0) {
      this.handlePreviousStep();
    }
  }

  handleNextButtonClick(event) {
    if (this.currentStep < this.fieldsets.length - 1) {
      this.dogNamesValidate();
      this.dogBreed();
      this.dogWeight();

      if (this.currentStepCheck(event)) {
        const nextStepIndex = this.getAdjacentStepIndex(1);
        this.showStep(nextStepIndex);
      }

      this.updateUI();
    }

    const currentFieldset = this.fieldsets[this.currentStep];
    const isSummaryStep =
      currentFieldset &&
      (currentFieldset.classList.contains("fieldset-summary") ||
        currentFieldset.dataset.step === "10");
    if (isSummaryStep) {
      this.populateTabForms();
      this.bindTabs();
    }
  }

  handleKeyDown(event) {
    if (event.defaultPrevented) return;
    if (!this.isFmfKeyboardNavigationActive()) return;

    if (event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    const eventTarget = event.target;
    const key = event.key || "";
    const isEnterKey = key === "Enter" || event.keyCode === 13;

    if (isEnterKey && this.shouldEnterAdvanceStep(eventTarget)) {
      if (
        this.nextButton &&
        this.nextButton.offsetParent !== null &&
        !this.nextButton.classList.contains("hide")
      ) {
        event.preventDefault();
        this.nextButton.click();
      }
      return;
    }

    const isInputLikeTarget =
      eventTarget &&
      typeof eventTarget.closest === "function" &&
      eventTarget.closest('input, textarea, select, [contenteditable="true"]');
    if (isInputLikeTarget) {
      return;
    }

    if (key === "ArrowRight" || event.keyCode === 39) {
      if (
        this.nextButton &&
        this.nextButton.offsetParent !== null &&
        !this.nextButton.classList.contains("hide")
      ) {
        event.preventDefault();
        this.nextButton.click();
      }
      return;
    }

    if (key === "ArrowLeft" || event.keyCode === 37) {
      if (this.prevButton && this.prevButton.offsetParent !== null) {
        event.preventDefault();
        this.prevButton.click();
      }
    }
  }

  isFmfKeyboardNavigationActive() {
    if (!this.fmfModal) {
      return false;
    }

    return (
      this.fmfModal.classList.contains("active") ||
      this.fmfModal.classList.contains("fmf-page-embed")
    );
  }

  shouldEnterAdvanceStep(eventTarget) {
    if (!eventTarget || typeof eventTarget.closest !== "function") {
      return false;
    }

    const activeFieldset = this.fieldsets?.[this.currentStep];
    if (!activeFieldset || activeFieldset.classList.contains("fieldset-summary")) {
      return false;
    }

    if (!activeFieldset.contains(eventTarget)) {
      return false;
    }

    if (eventTarget.closest('textarea, button, [contenteditable="true"], .breed-suggestions')) {
      return false;
    }

    const input = eventTarget.closest("input, select");
    if (!input || input.disabled) {
      return false;
    }

    const ignoredInputTypes = [
      "button",
      "checkbox",
      "file",
      "hidden",
      "image",
      "radio",
      "reset",
      "submit",
    ];
    return !ignoredInputTypes.includes(String(input.type || "").toLowerCase());
  }

  handleInputValidation(inputElements, errorMessage, validationCondition, event) {
    const normalizeElements = (elements) => {
      if (Array.isArray(elements)) return elements;
      if (elements instanceof NodeList || elements instanceof HTMLCollection) {
        return Array.from(elements);
      }
      return [elements];
    };

    const setRequiredAttribute = (elements, shouldSet) => {
      const targets = normalizeElements(elements);
      targets.forEach((element) => {
        if (!element || typeof element.setAttribute !== "function") return;
        if (shouldSet) {
          element.setAttribute("required", "required");
        } else {
          element.removeAttribute("required");
        }
      });
    };

    this.formErrorsElement.textContent = "";

    if (validationCondition) {
      setRequiredAttribute(inputElements, true);

      this.formErrorsElement.textContent = errorMessage;

      event.preventDefault();

      return true;
    } else {
      setRequiredAttribute(inputElements, false);
      this.formErrorsElement.textContent = "";
      return false;
    }
  }

  handleDogNameValidation(event) {
    const activeDogNameInput =
      this.getActiveTabInput('input[name="dogs[0][dog_name]"]') || this.dogNameInput;
    const dogName = activeDogNameInput?.value?.trim() || "";
    this.dogValues.dogName = dogName;

    if (
      this.handleInputValidation(
        activeDogNameInput,
        "Please fill out fields before progressing",
        dogName === "",
        event,
      )
    ) {
      return false;
    } else {
      return true;
    }
  }

  handleDogAgeSelectValidation(event) {
    this.dogValues.dogAge = this.dogAgeInput.value;
    const invalidDogAge = !this.dogValues.dogAge || this.dogValues.dogAge === "Please choose";

    if (
      this.handleInputValidation(
        this.dogAgeInput,
        "Please select your dog's age",
        invalidDogAge,
        event,
      )
    ) {
      return false;
    }

    return true;
  }

  handleDogBreedValidation(event) {
    this.updateDogBreedValue();
    const dogBreed = this.dogValues.dogBreed;
    const selectedBreedType = this.getSelectedBreedType();

    // Check if breed selection type is chosen
    if (!selectedBreedType) {
      if (
        this.handleInputValidation(
          this.breedSelectionInputs,
          "Please select a breed type",
          true,
          event,
        )
      ) {
        return false;
      }
    }

    // If single or multiple breed is selected, validate breed inputs
    if (selectedBreedType === "single" || selectedBreedType === "multiple") {
      const breedInputs = Array.from(this.dogBreedInputs).filter((input) => {
        if (selectedBreedType === "single") {
          return !!input.closest(".single-breed-container");
        }
        return !!input.closest(".multiple-breed-container");
      });

      if (
        this.handleInputValidation(
          breedInputs,
          "Please enter at least one breed",
          dogBreed === "",
          event,
        )
      ) {
        return false;
      }

      const invalidBreedInputs = breedInputs.filter((input) => {
        const value = input.value.trim();
        return value !== "" && !this.validateBreedInputAgainstList(input);
      });

      if (invalidBreedInputs.length > 0) {
        if (
          this.handleInputValidation(
            invalidBreedInputs,
            "Please select a breed from the list",
            true,
            event,
          )
        ) {
          return false;
        }
      }
    }

    this.formErrorsElement.textContent = "";
    return true;
  }

  handleDogAgeValidation(event) {
    return this.handleDogAgeSelectValidation(event) && this.handleDogBirthdayValidation(event);
  }

  handleDogBirthdayValidation(event) {
    this.dogValues.dogAge = this.dogAgeInput.value;

    if (this.dogValues.dogAge === "unknown") {
      this.dogValues.dogDob = "";
      this.dogDobInput.value = "";
      this.setCanonicalDogDob("");
      this.dogDobInput.removeAttribute("required");
      this.toggleUnderEightWeeksWarning(false);
      this.formErrorsElement.textContent = "";
      return true;
    }

    const normalizedDogDob = this.getCanonicalDogDob();
    if (normalizedDogDob) {
      this.dogDobInput.value =
        this.formatDogDobForDisplay(normalizedDogDob) || this.dogDobInput.value;
    }

    this.dogValues.dogDob = normalizedDogDob;

    if (
      this.handleInputValidation(
        this.dogDobInput,
        "Please enter your dog's date of birth",
        this.dogValues.dogDob === "",
        event,
      )
    ) {
      return false;
    }

    const dogAgeInWeeks = this.getDogAgeInWeeks(this.dogValues.dogDob);
    if (dogAgeInWeeks !== null && dogAgeInWeeks < 8) {
      this.toggleUnderEightWeeksWarning(true);
      return false;
    }

    this.toggleUnderEightWeeksWarning(false);

    return true;
  }

  handleDogWeightValidation(event) {
    const activeDogWeightInput =
      this.fieldsets[this.currentStep]?.querySelector(
        'input[name="dogs[0][dog_weight_current]"]',
      ) || this.dogWeightInput;
    const weight = parseFloat(activeDogWeightInput.value.trim());

    // Check if weight input is empty
    if (!Number.isFinite(weight) || weight <= 0) {
      if (
        this.handleInputValidation(
          activeDogWeightInput,
          "Please add your dog's weight to continue",
          true, // Always show the error message if weight is empty
          event,
        )
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      // Check if weight exceeds maximum
      if (weight > 70) {
        activeDogWeightInput.value = 70; // Cap the weight at 70 kg
        this.dogValues.dogWeight = 70;
        if (
          this.handleInputValidation(
            activeDogWeightInput,
            "The maximum weight limit is 70kg",
            true, // Always show the error message for exceeding max weight
            event,
          )
        ) {
          return false;
        } else {
          return true;
        }
      } else {
        // Weight input is valid, remove any existing error message
        this.handleInputValidation(
          activeDogWeightInput,
          "", // Clear the error message
          false, // Do not show the error message
          event,
        );
        this.dogValues.dogWeight = weight;
        return true;
      }
    }
  }

  handleDogFeedingPrefsValidation(event) {
    const isAnyPrefSelected = Array.from(this.dogFeedingPrefsRadios).some((radio) => radio.checked);
    let selectedPrefValue = null;

    this.dogFeedingPrefsRadios.forEach((radio) => {
      if (radio.checked) {
        const label = this.root.querySelector(`label[for="${radio.id}"]`);
        selectedPrefValue = label ? label.textContent : "";
        this.dogValues.dogFoodType = radio.id;
      }
    });

    if (selectedPrefValue !== null) {
      this.dogValues.tagArray = [];
      switch (this.dogValues.dogFoodType) {
        case "dog-feeding-wet-only":
          this.dogValues.tagArray.push("type:wet_food");
          break;
        case "dog-feeding-dry-only":
          this.dogValues.tagArray.push("type:dry_food");
          break;
        case "dog-feeding-mix":
          this.dogValues.tagArray.push("type:wet_food", "type:dry_food");
          break;
        default:
          break;
      }

      if (!this.shouldIncludeDryPreferenceStep()) {
        this.dogValues.dogDryFoodPreference = "";
        this.dogDryPreferenceRadios.forEach((radio) => {
          radio.checked = false;
        });
      }
    }

    this.formErrorsElement.textContent = "";

    let feedingInputArray = Array.from(this.dogFeedingPrefsRadios);

    if (
      this.handleInputValidation(
        feedingInputArray,
        "Please select a feeding preference",
        !isAnyPrefSelected,
        event,
      )
    ) {
      return false;
    } else {
      return true;
    }
  }

  handleDogDryFoodPrefsValidation(event) {
    if (!this.shouldIncludeDryPreferenceStep()) {
      return true;
    }

    const selectedDryPreference = Array.from(this.dogDryPreferenceRadios).find(
      (radio) => radio.checked,
    );
    this.dogValues.dogDryFoodPreference = selectedDryPreference ? selectedDryPreference.id : "";
    this.formErrorsElement.textContent = "";

    return !this.handleInputValidation(
      Array.from(this.dogDryPreferenceRadios),
      "Please select a dry food preference",
      !selectedDryPreference,
      event,
    );
  }

  handleDogFavouritesValidation(event) {
    let selectedFavouriteValues = [];
    let selectedFavouriteLabels = [];
    let selectedFavouritesString = "everything";

    // Clear previous favourites so unselecting all persists correctly.
    this.dogValues.dogFavourites = "";

    this.dogFavouritesCheckboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        // Get the label associated with the selected checkbox
        const label = this.root.querySelector(`label[for="${checkbox.id}"]`);
        if (label) {
          selectedFavouriteLabels.push(label.textContent.trim());
          this.dogValues.tagArray.push(checkbox.id);
        }
        selectedFavouriteValues.push(
          String(checkbox.value || "")
            .trim()
            .toLowerCase(),
        );
      }
    });

    if (selectedFavouriteValues.length > 0) {
      this.dogValues.dogFavourites = selectedFavouriteValues.join("|");
      const selectedFavouritesCopy = [...selectedFavouriteLabels];
      const lastElement = selectedFavouritesCopy.pop();

      if (selectedFavouritesCopy.length > 0) {
        selectedFavouritesString = selectedFavouritesCopy.join(", "); // Create a comma-separated string
        selectedFavouritesString += " and " + lastElement;
      } else {
        selectedFavouritesString = lastElement || "everything"; // Handle case where array could be empty
      }

      if (
        this.dogFavouritesCheckboxes.length > 0 &&
        Array.from(this.dogFavouritesCheckboxes).every((checkbox) => checkbox.checked)
      ) {
        selectedFavouritesString = "everything";
      }
    }

    this.dogFavouritesElements.forEach((element) => {
      element.textContent = selectedFavouritesString;
    });

    return true;
  }

  handleDogActivityValidation(event) {
    const activeDogActivityInputs = Array.from(
      this.fieldsets[this.currentStep]?.querySelectorAll('input[name="dogs[0][dog_activity]"]') ||
        this.dogActiveInput,
    );
    const checkedRadio = activeDogActivityInputs.find((radio) => radio.checked);
    const isAnyRadioChecked = Boolean(checkedRadio);

    if (
      this.handleInputValidation(
        activeDogActivityInputs,
        "Please enter your dog's activity level",
        !isAnyRadioChecked,
        event,
      )
    ) {
      return false;
    } else {
      this.dogValues.dogActive = checkedRadio.value;
      this.activityLevel = checkedRadio.id;

      const activityIndex = this.resolveActivityAgeIndex({
        dogAge: this.dogAgeInput.value,
        dogActive: this.dogValues.dogActive,
        activityLevelId: this.activityLevel,
      });
      const hasActivityData = this.syncActivityAgeAndWeightFoodData(activityIndex);
      if (!hasActivityData) {
        this.formErrorsElement.textContent =
          "We couldn't calculate a recommendation for that weight. Please check the weight and try again.";
        event.preventDefault();
        return false;
      }

      const totalKcalRequirement = this.calculateTotalKcalRequirement({
        showPuppyDisclaimer: true,
      });
      if (totalKcalRequirement === null) {
        this.formErrorsElement.textContent =
          "We couldn't calculate a recommendation for that weight. Please check the weight and try again.";
        event.preventDefault();
        return false;
      }

      this.applyCalculatedFoodValues(totalKcalRequirement);

      const fmfData = this.dogValuesToObject();

      window.dispatchEvent(new CustomEvent("find_my_food", { detail: fmfData }));

      this.persistFMFData(fmfData);

      return true;
    }
  }

  ensureFoodDefaults() {
    this.dogValues.wetGrams = this.dogValues.wetGrams || 0;
    this.dogValues.wetGramsMin = this.dogValues.wetGramsMin || this.dogValues.wetGrams || 0;
    this.dogValues.wetGramsMax = this.dogValues.wetGramsMax || this.dogValues.wetGrams || 0;
    this.dogValues.dryGrams = this.dogValues.dryGrams || 0;
    this.dogValues.dryGramsMin = this.dogValues.dryGramsMin || this.dogValues.dryGrams || 0;
    this.dogValues.dryGramsMax = this.dogValues.dryGramsMax || this.dogValues.dryGrams || 0;
    this.dogValues.wetTrays = this.dogValues.wetTrays || "";
    this.dogValues.wetTraysMin = this.dogValues.wetTraysMin || this.dogValues.wetTrays || "";
    this.dogValues.wetTraysMax = this.dogValues.wetTraysMax || this.dogValues.wetTrays || "";
  }

  syncDogOptionalFieldsFromInputs() {
    // Keep breed and DOB in sync with the latest input state before building payloads.
    this.updateDogBreedValue();
    this.dogValues.dogDob = this.getCanonicalDogDob();

    this.dogValues.dogInstagram = this.dogInstagramInput.value;

    this.dogGenderInput.forEach((radioButton) => {
      if (radioButton.checked) {
        this.dogValues.dogGender = radioButton.value;
      }
    });

    this.dogNeuteredInput.forEach((radioButton) => {
      if (radioButton.checked) {
        this.dogValues.dogNeutered = radioButton.value;
      }
    });

    this.dogRescueInput.forEach((radioButton) => {
      if (radioButton.checked) {
        this.dogValues.dogRescue = radioButton.value;
      }
    });

    this.dogValues.dogSiblings = this.dogSiblingsInput.value;
    this.dogValues.dogBio = this.dogBioInput.value;
    this.dogValues.dogTiktok = this.dogTiktokInput.value;
    this.dogValues.dogImage = this.root.querySelector("[data-dog-image]").src;
  }

  buildPassportFormData(form, fmfData) {
    const formData = new FormData(form);
    formData.delete("dogs[0][dog_dob_display]");

    if (window.QF.customer) {
      formData.set("dogs[0][customer_id]", window.QF.customer.id);
    }

    const parsedDogSiblings = Number.parseInt(
      fmfData?.dog_siblings ?? formData.get("dogs[0][dog_siblings]"),
      10,
    );
    const normalizedDogSiblings =
      Number.isFinite(parsedDogSiblings) && parsedDogSiblings >= 0 ? parsedDogSiblings : 0;
    formData.set("dogs[0][dog_siblings]", String(normalizedDogSiblings));

    // The passport endpoint expects a single string value for dog_breed.
    const normalizedDogBreed = this.normalizeUnknownBreedValue(this.dogValues.dogBreed);
    const dogBreedForPassport = normalizedDogBreed !== "" ? normalizedDogBreed : "no_answer";
    formData.delete("dogs[0][dog_breed][]");
    formData.set("dogs[0][dog_breed]", dogBreedForPassport);

    // The passport endpoint expects Y-m-d.
    const dogDobForPassport = this.getCanonicalDogDob();
    if (dogDobForPassport) {
      formData.set("dogs[0][dog_dob]", dogDobForPassport);
    }

    const optionalPassportFields = {
      dog_bio: "dogs[0][dog_bio]",
      dog_instagram: "dogs[0][dog_instagram]",
      dog_tiktok: "dogs[0][dog_tiktok]",
    };

    Object.entries(optionalPassportFields).forEach(([fmfKey, formKey]) => {
      if (fmfData[fmfKey] === "no_answer") {
        formData.set(formKey, "no_answer");
      }
    });

    const fileInput = this.root.querySelector('input[type="file"]');

    if (fileInput.files[0]) {
      formData.set("dogs[0][dog_image]", fileInput.files[0]);
    } else {
      formData.delete("dogs[0][dog_image]");
    }

    formData.set("dogs[0][dog_preferences]", this.dogValues.dogFoodType);
    const dogDryPreferenceForPassport = this.dogValues.dogDryFoodPreference || "no_answer";
    formData.delete("dogs[0][dog_dry_preference]");
    formData.set("dogs[0][dog_dry_preferences]", dogDryPreferenceForPassport);
    formData.set("dogs[0][dog_activity]", this.dogValues.dogActive);

    // Keep calculated FMF outputs in the passport payload so the passport
    // upsert doesn't null out feeding results after a successful FMF save.
    const feedingFields = {
      dog_wet_grams: fmfData?.dog_wet_grams ?? 0,
      dog_wet_grams_min: fmfData?.dog_wet_grams_min ?? fmfData?.dog_wet_grams ?? 0,
      dog_wet_grams_max: fmfData?.dog_wet_grams_max ?? fmfData?.dog_wet_grams ?? 0,
      dog_dry_grams: fmfData?.dog_dry_grams ?? 0,
      dog_dry_grams_min: fmfData?.dog_dry_grams_min ?? fmfData?.dog_dry_grams ?? 0,
      dog_dry_grams_max: fmfData?.dog_dry_grams_max ?? fmfData?.dog_dry_grams ?? 0,
      dog_wet_trays: fmfData?.dog_wet_trays ?? 0,
      dog_wet_trays_min: fmfData?.dog_wet_trays_min ?? fmfData?.dog_wet_trays ?? 0,
      dog_wet_trays_max: fmfData?.dog_wet_trays_max ?? fmfData?.dog_wet_trays ?? 0,
      dog_daily_kcal: fmfData?.dog_daily_kcal ?? fmfData?.dog_daily_kcals ?? 0,
    };

    Object.entries(feedingFields).forEach(([field, value]) => {
      formData.set(`dogs[0][${field}]`, String(value ?? 0));
    });

    return formData;
  }

  submitPassportFormData(formData) {
    if (window.QF.logged_in) {
      this.postFormData(this.apiUrl("passport"), formData, {
        headers: {
          Accept: "application/json",
        },
        checkOk: true,
      }).catch((error) => {
        this.formErrorsElement.textContent =
          error.status === 422
            ? "Please upload a valid image file (jpeg, png, jpg, gif, svg) and the file size is no more than 2mb."
            : "There was an error submitting your form. Please try again.";
      });
    }
  }

  handleLastStep() {
    const selectedFoodPreference = Array.from(this.dogFeedingPrefsRadios || []).find(
      (radio) => radio.checked,
    );
    if (selectedFoodPreference) {
      this.dogValues.dogFoodType = selectedFoodPreference.id;
    }

    const selectedDryPreference = Array.from(this.dogDryPreferenceRadios || []).find(
      (radio) => radio.checked,
    );
    this.dogValues.dogDryFoodPreference = selectedDryPreference ? selectedDryPreference.id : "";

    const activityIndex = this.resolveActivityAgeIndex({
      dogAge: this.dogValues.dogAge || this.dogAgeInput?.value,
      dogActive: this.dogValues.dogActive,
      activityLevelId: this.activityLevel,
    });
    const hasActivityData = this.syncActivityAgeAndWeightFoodData(activityIndex);
    if (hasActivityData) {
      const totalKcalRequirement = this.calculateTotalKcalRequirement({
        showPuppyDisclaimer: false,
      });
      if (totalKcalRequirement !== null) {
        this.applyCalculatedFoodValues(totalKcalRequirement);
      }
    }

    this.ensureFoodDefaults();
    this.syncDogOptionalFieldsFromInputs();

    const fmfData = this.dogValuesToObject();
    const defaultPassportDog = this.getDefaultPassportDog();
    if (!this.getStoredJSON("FMF") && defaultPassportDog && this.isEmptyFMFSubmission(fmfData)) {
      this.syncFormStateToDog(defaultPassportDog);
      this.persistFMFData(defaultPassportDog);
      this.showExistingResults();
      return;
    }

    if (window.QF.customer) {
      this.postJSON(
        this.apiUrl("feeding-advice"),
        { dogs: [fmfData] },
        {
          checkOk: true,
          parseJson: false,
        },
      ).catch((error) => console.error("Error:", error));
    }

    this.persistFMFData(fmfData);
    this.persistPassportData(fmfData);

    window.dispatchEvent(new CustomEvent("pet_passport", { detail: fmfData }));

    const form = this.root.querySelector(".fmf-form form");
    const formData = this.buildPassportFormData(form, fmfData);
    this.submitPassportFormData(formData);

    this.initKlaviyoForm();
  }

  buildStepValidationHandlers() {
    return {
      1: (event) => this.handleDogNameValidation(event),
      "1-age": (event) => this.handleDogAgeSelectValidation(event),
      "1-birthday": (event) => this.handleDogBirthdayValidation(event),
      "1-details": () => true,
      2: (event) => this.handleDogBreedValidation(event),
      4: (event) => this.handleDogWeightValidation(event),
      "4-activity": (event) => this.handleDogActivityValidation(event),
      5: (event) => this.handleDogFeedingPrefsValidation(event),
      6: (event) => this.handleDogDryFoodPrefsValidation(event),
      7: (event) => this.handleDogFavouritesValidation(event),
      8: () => {
        // Optional info step: submit passport data before moving to summary.
        if (!this.skipFormSubmissionOnLoad) {
          this.handleLastStep();
        }
        return true;
      },
    };
  }

  currentStepCheck(event) {
    const currentFieldset = this.fieldsets[this.currentStep];
    const currentDataStep = currentFieldset?.dataset?.step;
    const stepHandler = this.stepValidationHandlers?.[currentDataStep];
    if (!stepHandler) return true;
    return stepHandler(event);
  }

  getWeightArray(weight) {
    if (weight > 1 && weight <= 6) {
      return [36.92, 43.57]; // puppy1to6
    } else if (weight > 6 && weight <= 15) {
      return [36.86, 48.22]; // puppy7to15
    } else if (weight > 15 && weight <= 27) {
      return [39.88, 60.7]; // puppy16to27
    } else if (weight > 27 && weight <= 47) {
      return [36.96, 56.18]; // puppy28to47
    } else if (weight > 47) {
      return [36.61, 62.39]; // puppy48to70
    } else {
      return [36.92, 43.57]; // Default value (puppy1to6)
    }
  }

  // Method to show or hide food options based on the selected dog food type
  updateFoodOptionsDisplay(dogFoodTypeValue, totalKcalRequirement) {
    const wetFoodSummary = this.root.querySelector("[fmf-form-summary-wet-food]");
    const dryFoodSummary = this.root.querySelector("[fmf-form-summary-dry-food]");
    const andElement = this.root.querySelector(".and");
    const wetFoodGrams = this.root.querySelector("[fmf-form-wet-daily]");
    const dryFoodGrams = this.root.querySelector("[fmf-form-dry-daily]");
    const wetFoodTrays = this.root.querySelector("[fmf-tray-wet-daily]");
    const dailyKcalElement = this.root.querySelector("[fmf-form-kcal-daily]");
    const rangeDisclaimer = this.root.querySelector("[fmf-form-range-disclaimer]");

    const currentDogAge = this.dogAgeInput?.value || this.dogValues.dogAge;
    const isPuppy = this.isPuppyAge(currentDogAge);
    const puppyActivityData = isPuppy ? this.getPuppyActivityData() : null;
    const puppyWetOverride = puppyActivityData?.["wet-default"]?.[0] || null;
    const puppyDryOverride = puppyActivityData?.["dry-default"]?.[0] || null;
    const seniorWetOverride = this.isSeniorAge(currentDogAge)
      ? this.getAdultSedentaryWetDefaultData()
      : null;
    const adultWetOverride =
      !isPuppy && !this.isSeniorAge(currentDogAge) ? this.getAdultWetDefaultData() : null;
    const wetDefaultData =
      seniorWetOverride ||
      puppyWetOverride ||
      adultWetOverride ||
      (!isPuppy ? this.activityAgeData?.["wet-default"]?.[0] : null) ||
      (!isPuppy ? this.getFirstAvailableDefaultData("wet-default") : null);
    const dryDefaultData =
      puppyDryOverride ||
      (!isPuppy ? this.activityAgeData?.["dry-default"]?.[0] : null) ||
      (!isPuppy ? this.getFirstAvailableDefaultData("dry-default") : null);

    if (!wetDefaultData || !dryDefaultData) {
      return {
        wetGrams: 0,
        dryGrams: 0,
        wetTrays: "",
        wetTraysMin: "",
        wetTraysMax: "",
        wetGramsMin: 0,
        wetGramsMax: 0,
        dryGramsMin: 0,
        dryGramsMax: 0,
        dailyKcal: Number.isFinite(totalKcalRequirement) ? Math.round(totalKcalRequirement) : 0,
      };
    }
    const activityAgeDataWet = Number(wetDefaultData["CEN ME(kcal/100g)"]);
    const activityAgeDataWetLowest =
      Number(wetDefaultData["Lowest per 100g"]) || activityAgeDataWet;
    const activityAgeDataWetHighest =
      Number(wetDefaultData["Highest per 100g"]) || activityAgeDataWet;
    const activityAgeDataDry = Number(dryDefaultData["CEN ME(kcal/100g)"]);
    const activityAgeDataDryLowest =
      Number(dryDefaultData["Lowest per 100g"]) || activityAgeDataDry;
    const activityAgeDataDryHighest =
      Number(dryDefaultData["Highest per 100g"]) || activityAgeDataDry;
    const roundedDailyKcal = Number.isFinite(totalKcalRequirement)
      ? Math.round(totalKcalRequirement)
      : 0;

    if (dailyKcalElement) {
      dailyKcalElement.textContent = roundedDailyKcal;
      const dailyKcalRow = dailyKcalElement.closest(".result-kcal");
      if (dailyKcalRow) {
        dailyKcalRow.style.display = roundedDailyKcal > 0 ? "" : "none";
      }
    }

    let totalGramsWet = 0;
    let totalGramsDry = 0;
    let wetGrams = 0;
    let wetGramsMin = 0;
    let wetGramsMax = 0;
    let dryGrams = 0;
    let dryGramsMin = 0;
    let dryGramsMax = 0;
    let wetTrays = "";
    let wetTraysMin = "";
    let wetTraysMax = "";
    let wetRange;
    let wetRangeMix;
    let wetTrayRange;
    let wetTrayRangeMix;
    let dryRange;
    let dryRangeMix;
    let shouldShowRangeDisclaimer = false;

    const calculateGrams = (kcalRequirement, kcalPer100g) =>
      Math.round((kcalRequirement / kcalPer100g) * 100);

    const calculateWetGramsRange = (kcalRequirement) => {
      const minGrams = calculateGrams(kcalRequirement, activityAgeDataWetHighest);
      const maxGrams = calculateGrams(kcalRequirement, activityAgeDataWetLowest);
      return {
        minGrams,
        maxGrams,
        display: minGrams === maxGrams ? `${minGrams}g` : `${minGrams}-${maxGrams}g`,
      };
    };

    const calculateWetTrayRange = (minGrams, maxGrams) => {
      const minTrays = (minGrams / 395).toFixed(2);
      const maxTrays = (maxGrams / 395).toFixed(2);
      return {
        minTrays,
        maxTrays,
        display: minTrays === maxTrays ? minTrays : `${minTrays} - ${maxTrays}`,
      };
    };

    const calculateDryGramsRange = (kcalRequirement) => {
      const minGrams = calculateGrams(kcalRequirement, activityAgeDataDryHighest);
      const maxGrams = calculateGrams(kcalRequirement, activityAgeDataDryLowest);
      return {
        minGrams,
        maxGrams,
        display: minGrams === maxGrams ? `${minGrams}g` : `${minGrams}-${maxGrams}g`,
      };
    };

    const normalizedDryPreference = String(this.dogValues.dogDryFoodPreference || "")
      .trim()
      .toLowerCase();
    const isLightlyBakedDryPreference =
      normalizedDryPreference === "dog-dry-pref-lightly-baked" ||
      normalizedDryPreference === "lightly_baked";
    const isColdPressedDryPreference =
      normalizedDryPreference === "dog-dry-pref-cold-pressed" ||
      normalizedDryPreference === "cold_pressed";
    const lightlyBakedHalfGrams = Number(this.weightFoodData?.["grams-of-lightly-baked-50-50"]);
    const coldPressedHalfGrams = Number(this.weightFoodData?.["grams-of-coldpress-50-50"]);
    const preferredHalfDryGrams =
      isLightlyBakedDryPreference &&
      Number.isFinite(lightlyBakedHalfGrams) &&
      lightlyBakedHalfGrams > 0
        ? lightlyBakedHalfGrams
        : isColdPressedDryPreference &&
            Number.isFinite(coldPressedHalfGrams) &&
            coldPressedHalfGrams > 0
          ? coldPressedHalfGrams
          : null;
    const shouldUsePreferredHalfDryGrams =
      !this.isPuppyAge(currentDogAge) && Number.isFinite(preferredHalfDryGrams);
    const preferredDryKcalPer100g =
      Number.isFinite(preferredHalfDryGrams) &&
      Number.isFinite(Number(this.weightFoodData?.["kcal-required"])) &&
      Number(this.weightFoodData?.["kcal-required"]) > 0
        ? (Number(this.weightFoodData["kcal-required"]) / (preferredHalfDryGrams * 2)) * 100
        : null;
    const shouldUsePreferredDryKcalPer100g =
      this.isPuppyAge(currentDogAge) && Number.isFinite(preferredDryKcalPer100g);

    const displayFoodSummary = (showWet, showDry, showAnd) => {
      wetFoodSummary.style.display = showWet ? "flex" : "none";
      dryFoodSummary.style.display = showDry ? "flex" : "none";
      andElement.style.display = showAnd ? "flex" : "none";
    };

    switch (dogFoodTypeValue) {
      case "dog-feeding-wet-only":
        totalGramsWet = calculateGrams(totalKcalRequirement, activityAgeDataWet);
        wetGrams = totalGramsWet;
        wetRange = calculateWetGramsRange(totalKcalRequirement);
        if (this.isSeniorAge(currentDogAge)) {
          const seniorWetOnlyGrams = Number(this.weightFoodData?.["grams-of-food"]);
          if (Number.isFinite(seniorWetOnlyGrams) && seniorWetOnlyGrams > 0) {
            totalGramsWet = Math.round(seniorWetOnlyGrams);
            wetGrams = totalGramsWet;
            wetRange.minGrams = totalGramsWet;
            wetRange.maxGrams = totalGramsWet;
          } else {
            totalGramsWet = wetRange.minGrams;
            wetGrams = totalGramsWet;
            wetRange.maxGrams = wetRange.minGrams;
          }
        }
        wetGramsMin = wetRange.minGrams;
        wetGramsMax = wetRange.maxGrams;
        wetTrayRange = calculateWetTrayRange(wetRange.minGrams, wetRange.maxGrams);
        wetTrays = (totalGramsWet / 395).toFixed(2);
        wetTraysMin = wetTrayRange.minTrays;
        wetTraysMax = wetTrayRange.maxTrays;

        if (wetFoodSummary) {
          displayFoodSummary(true, false, false);
          wetFoodGrams.innerHTML = this.formatGramsWithAverageAndRange(
            totalGramsWet,
            wetRange.minGrams,
            wetRange.maxGrams,
          );
          wetFoodTrays.innerHTML = wetTrayRange.display;
        }
        shouldShowRangeDisclaimer = this.hasDistinctGramsRange(
          wetRange.minGrams,
          wetRange.maxGrams,
        );
        break;

      case "dog-feeding-dry-only":
        totalGramsDry = shouldUsePreferredDryKcalPer100g
          ? calculateGrams(totalKcalRequirement, preferredDryKcalPer100g)
          : shouldUsePreferredHalfDryGrams
            ? Math.round(preferredHalfDryGrams * 2)
            : calculateGrams(totalKcalRequirement, activityAgeDataDry);
        dryGrams = totalGramsDry;

        dryRange = shouldUsePreferredDryKcalPer100g
          ? { minGrams: totalGramsDry, maxGrams: totalGramsDry, display: `${totalGramsDry}g` }
          : shouldUsePreferredHalfDryGrams
            ? { minGrams: totalGramsDry, maxGrams: totalGramsDry, display: `${totalGramsDry}g` }
            : calculateDryGramsRange(totalKcalRequirement);
        dryGramsMin = dryRange.minGrams;
        dryGramsMax = dryRange.maxGrams;

        if (dryFoodSummary) {
          displayFoodSummary(false, true, false);
          dryFoodGrams.innerHTML = this.formatGramsWithAverageAndRange(
            totalGramsDry,
            dryRange.minGrams,
            dryRange.maxGrams,
          );
        }
        shouldShowRangeDisclaimer = this.hasDistinctGramsRange(
          dryRange.minGrams,
          dryRange.maxGrams,
        );
        break;

      case "dog-feeding-mix":
        totalGramsWet = Math.round(calculateGrams(totalKcalRequirement, activityAgeDataWet) / 2);
        totalGramsDry = shouldUsePreferredDryKcalPer100g
          ? calculateGrams(totalKcalRequirement / 2, preferredDryKcalPer100g)
          : shouldUsePreferredHalfDryGrams
            ? Math.round(preferredHalfDryGrams)
            : Math.round(calculateGrams(totalKcalRequirement, activityAgeDataDry) / 2);
        wetRangeMix = calculateWetGramsRange(totalKcalRequirement / 2);
        wetGramsMin = wetRangeMix.minGrams;
        wetGramsMax = wetRangeMix.maxGrams;
        wetTrayRangeMix = calculateWetTrayRange(wetRangeMix.minGrams, wetRangeMix.maxGrams);
        wetTraysMin = wetTrayRangeMix.minTrays;
        wetTraysMax = wetTrayRangeMix.maxTrays;

        dryRangeMix = shouldUsePreferredDryKcalPer100g
          ? { minGrams: totalGramsDry, maxGrams: totalGramsDry, display: `${totalGramsDry}g` }
          : shouldUsePreferredHalfDryGrams
            ? { minGrams: totalGramsDry, maxGrams: totalGramsDry, display: `${totalGramsDry}g` }
            : calculateDryGramsRange(totalKcalRequirement / 2);
        dryGramsMin = dryRangeMix.minGrams;
        dryGramsMax = dryRangeMix.maxGrams;

        dryGrams = totalGramsDry;
        wetGrams = totalGramsWet;
        wetTrays = (totalGramsWet / 395).toFixed(2);

        if (wetFoodSummary && dryFoodSummary) {
          displayFoodSummary(true, true, true);
          wetFoodGrams.innerHTML = this.formatGramsWithAverageAndRange(
            totalGramsWet,
            wetRangeMix.minGrams,
            wetRangeMix.maxGrams,
          );
          wetFoodTrays.innerHTML = wetTrayRangeMix.display;
          dryFoodGrams.innerHTML = this.formatGramsWithAverageAndRange(
            totalGramsDry,
            dryRangeMix.minGrams,
            dryRangeMix.maxGrams,
          );
        }
        shouldShowRangeDisclaimer =
          this.hasDistinctGramsRange(wetRangeMix.minGrams, wetRangeMix.maxGrams) ||
          this.hasDistinctGramsRange(dryRangeMix.minGrams, dryRangeMix.maxGrams);
        break;

      default:
        if (wetFoodSummary && dryFoodSummary && andElement) {
          displayFoodSummary(true, true, true);
        }
    }

    this.toggleRangeDisclaimer(rangeDisclaimer, shouldShowRangeDisclaimer);

    return {
      wetGrams,
      dryGrams,
      wetTrays,
      wetTraysMin,
      wetTraysMax,
      wetGramsMin,
      wetGramsMax,
      dryGramsMin,
      dryGramsMax,
      dailyKcal: roundedDailyKcal,
    };
  }

  dogNamesValidate() {
    const activeTabContent = this.getActiveTabContent();
    const activeDogNameInput =
      this.getActiveTabInput('input[name="dogs[0][dog_name]"]') || this.dogNameInput;
    const dogName = activeDogNameInput?.value?.trim() || "";

    if (dogName !== "") {
      this.dogValues.dogName = dogName;

      // Always update the live form copy placeholders shown across the FMF steps.
      this.fmfForm?.querySelectorAll("[fmf-form-dog-name]").forEach((element) => {
        element.textContent = this.dogValues.dogName;
      });

      const isSummaryActive = Boolean(this.root.querySelector(".fieldset-summary.active"));
      if (!isSummaryActive) {
        return;
      }

      // Only update summary UI when the summary step is active to avoid cross-tab overwrites.
      activeTabContent?.querySelectorAll("[fmf-form-dog-name]").forEach((element) => {
        element.textContent = this.dogValues.dogName;
      });

      this.root
        .querySelectorAll(".fieldset-summary .account-tabs [fmf-form-dog-name]")
        .forEach((element) => {
          element.textContent = this.dogValues.dogName;
        });

      const activeTabId = activeTabContent?.id;
      if (!activeTabId) {
        return;
      }

      const activeTabNameElement = this.root.querySelector(
        `[data-change-tab][data-tab-name="${activeTabId}"] [fmf-form-dog-name]`,
      );
      if (activeTabNameElement) {
        activeTabNameElement.textContent = this.dogValues.dogName;
      }
    }
  }

  dogBreed() {
    this.updateDogBreedValue();

    if (this.dogValues.dogBreed !== "") {
      // Update any display elements that show the breed
      const breedDisplayElements = this.root.querySelectorAll("[data-dog-breed]");
      breedDisplayElements.forEach((element) => {
        element.textContent = this.dogValues.dogBreed;
      });
    }
  }
  dogWeight() {
    // Dog weight
    if (this.dogWeightInput.value !== "") {
      this.dogValues.dogWeight = this.dogWeightInput.value.trim();
    }
  }

  getNewDogAgeValue(dogAgeValue, dogDob) {
    if (dogAgeValue === "1-7 years adult") {
      return "Adult";
    } else if (dogAgeValue === "7+ senior") {
      return "Senior";
    } else if (dogAgeValue === "8 - 52 weeks puppy") {
      const inputAge = this.parseDogDob(this.normalizeDogDob(dogDob));
      if (!inputAge) {
        return "";
      }

      const diffTime = Math.abs(this.today - inputAge);
      const diffWeeks = Math.floor(diffTime / this.aWeek);

      return `${diffWeeks} Weeks Old`;
    }
    return "";
  }

  getSelectedFavourites(favourites) {
    let favouritesArray = [];

    // Check if favourites is already an array or a string
    if (Array.isArray(favourites)) {
      favouritesArray = favourites; // Use directly if it's an array
    } else if (typeof favourites === "string") {
      favouritesArray = favourites.split("|"); // Split if it's a string
    }

    const selectedFavourites = favouritesArray.filter((checkbox) => {
      const checkboxElement = this.root.querySelector(`#dog-favourite-${checkbox}`);
      if (checkboxElement) {
        return true;
      }
      return false;
    });

    if (selectedFavourites.length > 1) {
      return `${selectedFavourites.slice(0, -1).join(", ")} and ${selectedFavourites[selectedFavourites.length - 1]}`;
    } else if (selectedFavourites.length === 1) {
      return selectedFavourites[0];
    }
    return "everything";
  }

  checkExistingEmail() {
    const email = this.emailInput.value;

    this.postJSON(this.apiUrl("email-exists"), { email })
      .then(({ data }) => {
        if (data.exists === true) {
          this.root.querySelector(".exists").innerHTML =
            "Email already exists. Please login to continue.";
          this.createAccountBtn.disabled = true;
        } else {
          this.root.querySelector(".exists").innerHTML = "";
          this.createAccountBtn.disabled = false;
        }
      })
      .catch((error) => console.error("Error:", error));
  }

  handleResetClick(event) {
    event.preventDefault();
    this.resetFMF();
  }

  showExistingResults(event) {
    event?.preventDefault();
    const dogs = this.getStoredPassportDogs();
    if (!dogs.length) {
      return;
    }

    this.populateTabForms();
    this.bindTabs();

    const summaryIndex = Array.from(this.fieldsets || []).findIndex((fieldset) =>
      fieldset.classList.contains("fieldset-summary"),
    );
    if (summaryIndex >= 0) {
      this.showStep(summaryIndex);
      this.updateUI();
      this.updateFooterResultActions(true);
    }
  }

  resetFMF() {
    localStorage.removeItem("FMF");
    this.initialStorageSetup();

    this.nextButton.style.display = "inline";

    this.showStep(0);
    this.dogValues.dogFoodType = "";
    this.dogValues.dogDryFoodPreference = "";

    this.updateUI();

    const fieldsetInputs = this.root.querySelectorAll(
      '.fieldset input[type="text"], .fieldset input[type="number"], .fieldset input[type="date"], textarea',
    );
    fieldsetInputs.forEach((input) => {
      input.value = "";
    });
    this.dogValues.dogName = "";
    this.fmfForm?.querySelectorAll(".fieldset [fmf-form-dog-name]").forEach((element) => {
      element.textContent = this.defaultDogName;
    });
    this.setCanonicalDogDob("");

    this.dogAgeInput.selectedIndex = 0;

    // Clear checkboxes and radio buttons
    this.root
      .querySelectorAll('.fieldset input[type="checkbox"], .fieldset input[type="radio"]')
      .forEach((input) => {
        input.checked = false;
      });
    const unknownBreedCheckbox = this.root.querySelector("#unknownBreed");
    if (unknownBreedCheckbox) {
      unknownBreedCheckbox.checked = false;
    }
    this.setBreedChooserVisibility(true);
    this.root.querySelectorAll(".single-breed-container").forEach((singleContainer) => {
      singleContainer.style.display = "block";
    });
    this.syncBreedSelectionState();

    this.dogFavouritesCheckboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.dogValues.dogFavourites = "";
    this.dogFavouritesElements.forEach((element) => {
      element.textContent = "everything";
    });

    // Reset image
    this.dogImage.src = this.passportImgSrc;

    if (this.emailMessage) {
      this.emailMessage.innerHTML = "";
    }

    const updatedDogId = Date.now();
    this.dogValues.dogId = parseInt(updatedDogId);
    this.root.querySelector("[set-fmf-form-dog-id]").value = updatedDogId;
    this.updateFooterResultActions(false);
  }

  dogValuesToObject() {
    const normalizedDogDob =
      this.getCanonicalDogDob() || this.normalizeDogDob(this.dogValues.dogDob || "");
    const normalizedDogAge =
      this.normalizeDogAge(this.dogValues.dogAge) ||
      this.normalizeDogAge(this.dogAgeInput?.value || "") ||
      this.deriveDogAgeFromDob(normalizedDogDob);
    const parsedDogWeight = Number(this.dogValues.dogWeight);
    const normalizedDogWeight =
      Number.isFinite(parsedDogWeight) && parsedDogWeight > 0
        ? String(parsedDogWeight)
        : "no_answer";
    const parsedDogSiblings = Number.parseInt(this.dogValues.dogSiblings, 10);
    const normalizedDogSiblings =
      Number.isFinite(parsedDogSiblings) && parsedDogSiblings >= 0 ? parsedDogSiblings : 0;

    return {
      customer_id: window.QF.customer?.id || null,
      id: this.dogValues.dogId || null,
      dog_image: this.dogValues.dogImage || this.passportImgSrc,
      dog_name: this.dogValues.dogName || "no_answer",
      dog_dob: normalizedDogDob || "no_answer",
      dog_wet_grams: this.dogValues.wetGrams || 0,
      dog_wet_grams_min: this.dogValues.wetGramsMin || this.dogValues.wetGrams || 0,
      dog_wet_grams_max: this.dogValues.wetGramsMax || this.dogValues.wetGrams || 0,
      dog_dry_grams: this.dogValues.dryGrams || 0,
      dog_dry_grams_min: this.dogValues.dryGramsMin || this.dogValues.dryGrams || 0,
      dog_dry_grams_max: this.dogValues.dryGramsMax || this.dogValues.dryGrams || 0,
      dog_wet_trays: this.dogValues.wetTrays || "no_answer",
      dog_wet_trays_min: this.dogValues.wetTraysMin || this.dogValues.wetTrays || "no_answer",
      dog_wet_trays_max: this.dogValues.wetTraysMax || this.dogValues.wetTrays || "no_answer",
      dog_daily_kcal: this.dogValues.dailyKcal || 0,
      dog_breed: (() => {
        const normalizedDogBreed = this.normalizeUnknownBreedValue(this.dogValues.dogBreed);
        if (!normalizedDogBreed) return "no_answer";
        if (normalizedDogBreed === "unknown") return "unknown";
        return normalizedDogBreed.replace(/\s/g, "_").toLowerCase();
      })(),
      dog_age: normalizedDogAge || "",
      dog_weight_current: normalizedDogWeight,
      dog_preferences: this.dogValues.dogFoodType || "no_answer",
      dog_dry_preference: this.dogValues.dogDryFoodPreference || "no_answer",
      dog_favourites:
        typeof this.dogValues.dogFavourites === "string" &&
        this.dogValues.dogFavourites.trim() !== ""
          ? this.dogValues.dogFavourites.toLowerCase()
          : "all recipes",
      dog_activity:
        this.dogValues.dogActive && this.dogValues.dogActive.trim() !== ""
          ? this.dogValues.dogActive.trim().replace(/\s/g, "_").toLowerCase()
          : "no_answer",
      dog_gender: this.dogValues.dogGender || "no_answer",
      dog_neutered: this.dogValues.dogNeutered || "no_answer",
      dog_rescue: this.dogValues.dogRescue || "no_answer",
      dog_siblings: normalizedDogSiblings,
      dog_bio: this.dogValues.dogBio || "no_answer",
      dog_tiktok: this.dogValues.dogTiktok || "no_answer",
      dog_instagram: this.dogValues.dogInstagram || "no_answer",
      dog_passport_status: this.dogValues.dogPassportStatus || "no_answer",
    };
  }

  fetchCustomerToken() {
    this.postJSON(
      this.apiUrl("customer/auth"),
      {
        email: window.QF.customer.email,
        shopify_id: window.QF.customer.id,
      },
      {
        mode: "no-cors",
        contentType: null,
        parseJson: false,
      },
    ).catch((error) => console.error("Error:", error));
  }

  generateCollectionUrl(dogData) {
    const sourceData = dogData || this.getStoredJSON("FMF", {}) || {};
    this.RecommendedCollectionURL = this.baseUrlCollections;

    const dogAge = sourceData.dog_age;
    const dogFavourites = sourceData.dog_favourites;
    const dogPreferences = sourceData.dog_preferences;
    const normalizedDogPreference = String(dogPreferences || "")
      .trim()
      .toLowerCase();
    const dogDryPreference = String(
      sourceData.dog_dry_preference || sourceData.dog_dry_preferences || "",
    )
      .trim()
      .toLowerCase();

    // Age Filters
    if (dogAge) {
      if (dogAge === "1-7 years adult") {
        this.RecommendedCollectionURL += "&filter.p.m.filter.lifestage_filter=Adult";
      } else if (dogAge === "7+ senior") {
        this.RecommendedCollectionURL += "&filter.p.m.filter.lifestage_filter=Senior";
      } else {
        this.RecommendedCollectionURL += "&filter.p.m.filter.lifestage_filter=Puppy";
      }
    }

    // Favourites / Flavours Filters
    if (typeof dogFavourites === "string" && dogFavourites) {
      const normalizedFavourites = dogFavourites.trim().toLowerCase();

      if (normalizedFavourites !== "all recipes") {
        const favouriteFilterMap = {
          beef: ["Beef"],
          chicken: ["Chicken"],
          turkey: ["Turkey"],
          duck: ["Duck"],
          lamb: ["Lamb"],
          fish: ["Ocean Fish", "Salmon", "Sardines", "White Fish"],
          "ocean fish": ["Ocean Fish"],
          salmon: ["Salmon"],
          sardines: ["Sardines"],
          "white fish": ["White Fish"],
          herring: ["Herring"],
          offal: ["Liver", "Chicken Liver"],
          liver: ["Liver"],
          "chicken liver": ["Chicken Liver"],
          game: ["Venison", "Wild Boar", "Goose"],
          venison: ["Venison"],
          "wild boar": ["Wild Boar"],
          goose: ["Goose"],
        };

        const mappedFavourites = dogFavourites
          .split("|")
          .map((value) => String(value).trim().toLowerCase())
          .filter(Boolean)
          .flatMap((value) => favouriteFilterMap[value] || []);

        [...new Set(mappedFavourites)].forEach((favouriteFood) => {
          this.RecommendedCollectionURL += `&filter.p.m.filter.flavour_ingredients_filter=${encodeURIComponent(favouriteFood)}`;
        });
      }
    }

    const isWetOnlyPreference = normalizedDogPreference === "dog-feeding-wet-only";
    const isDryOnlyPreference = normalizedDogPreference === "dog-feeding-dry-only";
    const isMixedPreference =
      normalizedDogPreference === "dog-feeding-mix" ||
      normalizedDogPreference.includes("mixture") ||
      normalizedDogPreference.includes("mix");
    const isColdPressed =
      dogDryPreference === "dog-dry-pref-cold-pressed" || dogDryPreference === "cold_pressed";
    const isLightlyBaked =
      dogDryPreference === "dog-dry-pref-lightly-baked" || dogDryPreference === "lightly_baked";
    const isBoth = dogDryPreference === "dog-dry-pref-both" || dogDryPreference === "both";
    const hasDryRangeFilter = isColdPressed || isLightlyBaked || isBoth;

    // Wet And Dry Filters
    if (isWetOnlyPreference) {
      this.RecommendedCollectionURL += "&filter.p.m.filter.product_type_filter=Wet+Food";
    } else if (isDryOnlyPreference) {
      if (!hasDryRangeFilter) {
        this.RecommendedCollectionURL += "&filter.p.m.filter.product_type_filter=Dry+Food";
      }
    } else if (isMixedPreference) {
      this.RecommendedCollectionURL +=
        "&filter.p.m.filter.product_type_filter=Wet+Food&filter.p.m.filter.product_type_filter=Dry+Food";
      if (!hasDryRangeFilter) {
        this.RecommendedCollectionURL += "&filter.p.m.filter.product_type_filter=Dry+Food";
      }
    } else if (normalizedDogPreference) {
      this.RecommendedCollectionURL +=
        "&filter.p.m.filter.product_type_filter=Wet+Food&filter.p.m.filter.product_type_filter=Dry+Food";
    }

    // Dry range filters (for dry-only and mixed journeys).
    const shouldApplyDryRangeFilter = isDryOnlyPreference || isMixedPreference;
    if (shouldApplyDryRangeFilter) {
      if (isColdPressed || isBoth) {
        this.RecommendedCollectionURL +=
          "&filter.p.m.filter.range_filter=Cold%20Pressed%20Dry%20Food";
      }

      if (isLightlyBaked || isBoth) {
        this.RecommendedCollectionURL +=
          "&filter.p.m.filter.range_filter=Lightly%20Baked%20Dry%20Food";
      }
    }

    // Wet food filter (for wet-food and mixed journeys)
    const shouldApplyWetFoodFilter = !isDryOnlyPreference || isMixedPreference;
    if (shouldApplyWetFoodFilter) {
      this.RecommendedCollectionURL += "&filter.p.m.filter.range_filter=Complete%20Wet%20Food";
    }

    if (sourceData.id) {
      this.RecommendedCollectionURL += `&dogId=${encodeURIComponent(sourceData.id)}`;
    }

    const explicitDailyKcal = Number(sourceData.dog_daily_kcal);
    const calculatedDailyKcal = this.calculateDailyKcalForDogData(sourceData);
    const dogDailyKcal = Number.isFinite(explicitDailyKcal)
      ? explicitDailyKcal
      : calculatedDailyKcal;

    if (Number.isFinite(dogDailyKcal) && dogDailyKcal > 0) {
      this.RecommendedCollectionURL += `&dogDailyKcal=${encodeURIComponent(Math.round(dogDailyKcal))}`;
    }
  }

  initKlaviyoForm() {
    if (this.klaviyoFormBound) return;

    const isValidEmail = (email) => {
      const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return pattern.test(email);
    };

    const getEmailContainer = (element) => {
      return (
        element?.closest(".fmf-klaviyo-form-wrapper")?.querySelector(".email-recommendations") ||
        element?.closest(".email-recommendations")
      );
    };

    const getConsentField = (emailContainer) => {
      return emailContainer
        ?.closest(".fmf-klaviyo-form-wrapper")
        ?.querySelector('input[name="fmf-email-marketing-consent"]');
    };

    const syncConsentLabelState = (consentField) => {
      const consentLabel = consentField
        ?.closest(".fmf-marketing-consent")
        ?.querySelector("[fmf-marketing-consent-label]");
      consentLabel?.setAttribute("aria-checked", consentField.checked ? "true" : "false");
    };

    const updateSendButtonState = (emailField) => {
      if (!emailField) return;

      const emailContainer = getEmailContainer(emailField);
      const sendEmail = emailContainer?.querySelector("[fmf-send-email]");
      if (!sendEmail) return;

      const consentField = getConsentField(emailContainer);
      syncConsentLabelState(consentField);
      sendEmail.disabled = !isValidEmail(emailField.value) || !consentField?.checked;
    };

    const syncAllSendButtons = () => {
      this.root
        .querySelectorAll(".email-recommendations .klaviyo-email-input")
        .forEach((emailField) => {
          updateSendButtonState(emailField);
        });
    };

    this.root.addEventListener("input", (event) => {
      const emailField = event.target.closest(".klaviyo-email-input");
      if (!emailField) return;
      updateSendButtonState(emailField);
    });

    this.root.addEventListener("change", (event) => {
      const emailField = event.target.closest(".klaviyo-email-input");
      const consentField = event.target.closest('input[name="fmf-email-marketing-consent"]');

      if (emailField) {
        updateSendButtonState(emailField);
        return;
      }

      if (consentField) {
        const emailContainer = getEmailContainer(consentField);
        updateSendButtonState(emailContainer?.querySelector(".klaviyo-email-input"));
      }
    });

    const toggleMarketingConsent = (consentLabel) => {
      const consentField = consentLabel
        ?.closest(".fmf-marketing-consent")
        ?.querySelector('input[name="fmf-email-marketing-consent"]');
      if (!consentField) return;

      consentField.checked = !consentField.checked;
      consentField.dispatchEvent(new Event("change", { bubbles: true }));
    };

    this.root.addEventListener("keydown", (event) => {
      const consentLabel = event.target.closest("[fmf-marketing-consent-label]");
      if (!consentLabel || !["Enter", " "].includes(event.key)) return;

      event.preventDefault();
      toggleMarketingConsent(consentLabel);
    });

    const subscribeToEmailMarketing = async (emailContainer, email) => {
      const companyId = emailContainer?.dataset.klaviyoCompanyId;
      if (!companyId) {
        throw new Error("Missing Klaviyo company ID");
      }

      const listId = emailContainer?.dataset.klaviyoListId;
      if (!listId) {
        throw new Error("Missing Klaviyo list ID");
      }

      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`,
        {
          method: "POST",
          headers: {
            accept: "application/vnd.api+json",
            "content-type": "application/vnd.api+json",
            revision: "2026-04-15",
          },
          body: JSON.stringify({
            data: {
              type: "subscription",
              attributes: {
                custom_source: "Find My Food recommendations",
                profile: {
                  data: {
                    type: "profile",
                    attributes: {
                      email,
                      subscriptions: {
                        email: {
                          marketing: {
                            consent: "SUBSCRIBED",
                          },
                        },
                      },
                    },
                  },
                },
              },
              relationships: {
                list: {
                  data: {
                    type: "list",
                    id: listId,
                  },
                },
              },
            },
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Klaviyo marketing consent failed");
      }
    };

    const pushKlaviyo = (payload) =>
      new Promise((resolve, reject) => {
        const klaviyoObject = typeof klaviyo !== "undefined" ? klaviyo : window.klaviyo;
        if (!klaviyoObject?.push) {
          reject(new Error("Missing Klaviyo object"));
          return;
        }

        const timeout = window.setTimeout(() => {
          reject(new Error("Klaviyo tracking timed out"));
        }, 10000);

        klaviyoObject.push([
          ...payload,
          () => {
            window.clearTimeout(timeout);
            resolve();
          },
        ]);
      });

    this.root.addEventListener("click", async (event) => {
      const sendEmailButton = event.target.closest("[fmf-send-email]");
      if (!sendEmailButton) return;

      const recommendationsURL = this.RecommendedCollectionURL;
      if (!recommendationsURL) return;

      const emailContainer = sendEmailButton.closest(".email-recommendations");
      const emailField = emailContainer?.querySelector(".klaviyo-email-input");
      const consentField = getConsentField(emailContainer);
      const email = emailField?.value || "";
      if (!isValidEmail(email) || !consentField?.checked) return;

      const dogId = this.root.querySelector(".fmf-modal-form .fmf-form .tab-content.active")
        ?.dataset.dogId;
      const dogData = this.getStoredJSON("passport", []);
      const activeData = dogData.find((obj) => this.idsMatch(obj.id, dogId));

      event.preventDefault();
      const fmfdata = activeData || this.getStoredJSON("FMF", {});
      const resolvedDogId = dogId || fmfdata.id;
      const absoluteRecommendationsURL = new URL(
        recommendationsURL,
        window.location.origin,
      ).toString();
      const buildABoxURL = resolvedDogId
        ? new URL(
            `/pages/build-a-box?dogId=${encodeURIComponent(resolvedDogId)}`,
            window.location.origin,
          ).toString()
        : new URL("/pages/build-a-box", window.location.origin).toString();
      const dogDailyKcal =
        new URL(absoluteRecommendationsURL).searchParams.get("dogDailyKcal") ||
        fmfdata.dog_daily_kcal;
      const dogsForProfile = dogData.length ? dogData : [fmfdata].filter((dog) => dog?.dog_name);
      sendEmailButton.disabled = true;

      try {
        await pushKlaviyo([
          "identify",
          {
            $email: email,
            $consent: ["email"],
            "Accepts Marketing": true,
            DogCount: dogsForProfile.length,
            DogNames: dogsForProfile.map((dog) => dog.dog_name).filter(Boolean),
          },
        ]);

        await pushKlaviyo([
          "track",
          "Feeding Guide Results",
          {
            $email: email,
            "Accepts Marketing": true,
            DogId: resolvedDogId,
            DogName: fmfdata.dog_name,
            DogAge: fmfdata.dog_age,
            DogBreed: fmfdata.dog_breed,
            DogDOB: fmfdata.dog_dob,
            DogActivity: fmfdata.dog_activity,
            DogFavourites: fmfdata.dog_favourites,
            DogFoodPref: fmfdata.dog_preferences,
            DogDryFoodPref: fmfdata.dog_dry_preference,
            DogWeight: fmfdata.dog_weight_current,
            DogDryGrams: fmfdata.dog_dry_grams,
            DogWetGrams: fmfdata.dog_wet_grams,
            DogDailyKcal: dogDailyKcal,
            RecommendationURL: absoluteRecommendationsURL,
            BuildABoxURL: buildABoxURL,
          },
        ]);

        await subscribeToEmailMarketing(emailContainer, email);
      } catch (error) {
        console.error(error);
        sendEmailButton.disabled = false;
        emailContainer.closest(".fmf-klaviyo-form-wrapper")?.classList.add("inError");
        return;
      }

      if (emailContainer) {
        emailContainer
          .closest(".fmf-klaviyo-form-wrapper")
          ?.querySelector(".fmf-marketing-consent")
          ?.classList.add("hidden");
        emailContainer.innerHTML = "Thank you, your recommendations will be with you shortly.";
      }
    });

    syncAllSendButtons();
    this.klaviyoFormBound = true;
  }
}

class FeedingAdviceModal extends HTMLElement {
  connectedCallback() {
    // iOS Safari composites position:fixed with ancestor section layers
    // (Swiper translate3d, isolation). Move the slide-out host to <body>
    // so the tab can stack above page sections and below header/modals.
    if (this.shouldRelocateToBody()) {
      document.body.appendChild(this);
      return;
    }

    if (this.initialised) return;

    this.initialised = true;
    this.modal = this.querySelector(".fmf-modal");
    this.modalTab = this.querySelector(".fmf-modal .open-fmf");
    this.defaultModalText =
      this.modalTab?.dataset.title || this.modalTab?.textContent || "Feeding Advice";
    this.imagesLoaded = false;

    this.relocateEmbedTab();

    if (!this.modal) return;

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    document.addEventListener("click", this.handleDocumentClick);

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("feeding-advice")) {
      this.toggleModal(this.defaultModalText);
    }

    this.instance = new FeedingAdvice(this);
    window.FeedingAdvice = this.instance;
  }

  shouldRelocateToBody() {
    if (window.Shopify?.designMode) return false;
    if (this.querySelector(".fmf-page-embed")) return false;
    return Boolean(document.body) && this.parentElement !== document.body;
  }

  relocateEmbedTab() {
    if (window.Shopify?.designMode) return;
    const tab = this.modalTab;
    if (!tab?.classList.contains("anchor-link")) return;
    if (!document.body || tab.parentElement === document.body) return;
    tab.classList.add("fmf-floating-tab");
    document.body.appendChild(tab);
    this.observeEmbedTab(tab);
  }

  // The relocated tab only jumps down to the embedded form, so hide it while that
  // form sits level with the tab rather than letting it cover the content.
  observeEmbedTab(tab) {
    const embed = this.querySelector(".fmf-page-embed");
    if (!embed || !("IntersectionObserver" in window)) return;

    this.embedTabObserver = new IntersectionObserver(
      ([entry]) => {
        tab.classList.toggle("fmf-floating-tab--hidden", entry.isIntersecting);
      },
      { rootMargin: "-30% 0px -30% 0px" },
    );

    this.embedTabObserver.observe(embed);
  }

  disconnectedCallback() {
    this.embedTabObserver?.disconnect();

    if (this.handleDocumentClick) {
      document.removeEventListener("click", this.handleDocumentClick);
    }
  }

  handleDocumentClick(event) {
    const openTrigger = event.target.closest('[open-fmf-modal], a[href="#fmf-modal"]');

    if (openTrigger) {
      event.preventDefault();
      this.loadLazyImages();
      this.toggleModal(openTrigger.dataset.title || this.defaultModalText);
      return;
    }

    if (event.target.closest(".fmf-modal .close-modal")) {
      event.preventDefault();
      this.closeModal();
    }
  }

  loadLazyImages() {
    if (this.imagesLoaded) return;

    this.modal.querySelectorAll("img[data-src]").forEach((image) => {
      const src = image.getAttribute("data-src");

      if (src) {
        image.setAttribute("src", src);
        image.removeAttribute("data-src");
      }
    });

    this.imagesLoaded = true;
  }

  toggleModal(openModalText = this.defaultModalText) {
    document.body.classList.toggle("fixed");
    this.modal.classList.toggle("active");

    if (!this.modalTab) return;

    this.modalTab.textContent = this.modal.classList.contains("active") ? "Close" : openModalText;
  }

  closeModal() {
    document.body.classList.remove("fixed");
    this.modal.classList.remove("active");

    if (this.modalTab) {
      this.modalTab.textContent = this.defaultModalText;
    }
  }
}

if (!customElements.get("feeding-advice-modal")) {
  customElements.define("feeding-advice-modal", FeedingAdviceModal);
}

window.FeedingAdvice = FeedingAdvice;
window.FeedingAdviceModal = FeedingAdviceModal;
